"use client";

import { useState, useRef, useEffect, useCallback, forwardRef } from "react";
import { Heart, Search, Plus, Star, ChevronLeft, ChevronRight, X, ChevronDown, Minus, SlidersHorizontal, Check, Truck, Tag, Info, Menu, Package, CreditCard, Map as MapIcon, Sparkles } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  useStoreDetail,
  useStoreConfig,
  useStoreMenuCategories,
  useStoreMenuProducts,
  useStoreCategoryProductsDeep,
  useAllStoreProducts,
  useStoreMenuSearch,
  useStoreReviews,
  useProductAddons,
  fetchProductAddons,
  productAddonsKey,
  addonsRequireSelection,
} from "@/hooks/use-stores";
import { useQueryClient } from "@tanstack/react-query";
import { useToggleFavorite } from "@/hooks/use-favorites";
import { useAuthStore } from "@/store/auth.store";
import { useAppEnv } from "@/providers/app-env-provider";
import { useSearchStore } from "@/store/search.store";
import { useAddToCart, useStoreCart, useUpdateCartItem, useRemoveCartItem } from "@/hooks/use-cart";
import { findMatchingCartItem } from "@/components/cart/cart-helpers";
import { useCartAuthGate } from "@/components/layout/cart-auth-gate";
import { ReviewDetailModal } from "@/components/store/review-detail-modal";
import type { MenuCategory, MenuProduct, StoreReview, StoreConfig, StoreDetail, AddToCartPayload } from "@/types";
import dynamic from "next/dynamic";
import { storeSegment } from "@/lib/seo";
import { formatAmount } from "@/lib/money";
import { deliveryDisplay, distanceOrNull } from "@/lib/delivery";

// Lazy-load the store map: keeps maplibre-gl (~200 Ko de JS + CSS) hors du bundle de la fiche
// page's initial bundle; the chunk only loads when the info modal (the only
// place the map appears) opens.
const StoreLocationMap = dynamic(() => import("./store-location-map"), {
  ssr: false,
  loading: () => (
    <div className="h-[200px] w-full animate-pulse rounded-2xl bg-gray-100 dark:bg-[#262626]" />
  ),
});


/* ────────────── Helpers ────────────── */

function formatDistance(m: number | null) {
  // `distanceOrNull` écarte le zéro : le rendu serveur interroge l'API depuis
  // les coordonnées du magasin lui-même, ce qui donne 0 — et la fiche
  // affichait « 0 m ».
  const d = distanceOrNull(m);
  if (d == null) return null;
  return d < 1000 ? `${Math.round(d)} m` : `${(d / 1000).toFixed(1)} km`;
}


/* ──────────────────────────────────────────────────────────────
   Stock helpers.

   The menu endpoint returns `stock: 0` for EVERY product — real inventory
   counts simply aren't exposed there. So `stock === 0` must never be read as
   "out of stock": doing that wrongly marked every `inventory_enabled` product
   as Épuisé while the mobile app showed it as available. Availability comes
   from `status`; `stock` is only meaningful when it's a real positive count.
─────────────────────────────────────────────────────────────── */

function isProductOutOfStock(product: MenuProduct): boolean {
  return typeof product.status === "string" && product.status.toLowerCase() !== "active";
}

/** Real remaining stock, or null when the API isn't reporting a usable count. */
function trackedStock(product: MenuProduct): number | null {
  return product.inventory_enabled && product.stock > 0 ? product.stock : null;
}

/* ──────────────────────────────────────────────────────────────
   Shared "add to cart" control for product cards.
   - Products with a required addon group can't be added directly: the "+"
     opens the product modal so the customer picks the required option.
   - Products without required options get a − qty + stepper once in the cart.
─────────────────────────────────────────────────────────────── */

function useProductCartControl(storeUlid: string, product: MenuProduct, openModal: () => void) {
  const user = useAuthStore((s) => s.user);
  const cartAuth = useCartAuthGate();
  const queryClient = useQueryClient();
  const addToCart = useAddToCart();
  const updateItem = useUpdateCartItem();
  const removeItem = useRemoveCartItem();
  const { data: storeCart } = useStoreCart(storeUlid);

  // A product with required options can sit in the cart as several lines (one
  // per option combo), so the badge must total them all — not just the first.
  const productLines = storeCart?.items?.filter((i) => i.product_ulid === product.ulid) ?? [];
  const cartItem = productLines[0];
  const cartQty = productLines.reduce((sum, i) => sum + i.quantity, 0);

  // Fetch addon info for products already in the cart so we know whether to
  // render a stepper (no required options) or just the count (required → modal).
  const { data: addonsData } = useProductAddons(storeUlid, product.ulid, { enabled: cartQty > 0 });
  const requiresOptions = addonsData ? addonsRequireSelection(addonsData) : null;

  const [checking, setChecking] = useState(false);

  function directAdd() {
    const payload = {
      product_ulid: product.ulid,
      quantity: Math.max(1, product.minimum_quantity ?? 1),
      store_ulid: storeUlid,
      tracking: {
        name: product.name,
        price: product.discount_applied ? product.discounted_price : Number(product.price) || 0,
      },
    };
    if (!user) { cartAuth.requireAuth(payload, () => addToCart.mutate(payload)); return; }
    addToCart.mutate(payload);
  }

  async function onPlus(e?: React.MouseEvent) {
    e?.stopPropagation();
    if (requiresOptions === true) { openModal(); return; }
    if (cartItem && requiresOptions === false) {
      updateItem.mutate({ storeUlid, cartItemId: cartItem.cart_item_id, payload: { cart_item_id: cartItem.cart_item_id, quantity: cartItem.quantity + 1 } });
      return;
    }
    // Unknown (not in cart yet): fetch addons, then decide modal vs direct add.
    setChecking(true);
    try {
      const addons = await queryClient.fetchQuery({
        queryKey: productAddonsKey(storeUlid, product.ulid, !!user),
        queryFn: () => fetchProductAddons(storeUlid, product.ulid),
        staleTime: 2 * 60 * 1000,
      });
      if (addonsRequireSelection(addons)) openModal();
      else directAdd();
    } catch {
      directAdd();
    } finally {
      setChecking(false);
    }
  }

  function onMinus(e?: React.MouseEvent) {
    e?.stopPropagation();
    if (!cartItem) return;
    if (cartItem.quantity <= 1) {
      removeItem.mutate({ storeUlid, cartItemId: cartItem.cart_item_id });
    } else {
      updateItem.mutate({ storeUlid, cartItemId: cartItem.cart_item_id, payload: { cart_item_id: cartItem.cart_item_id, quantity: cartItem.quantity - 1 } });
    }
  }

  return {
    cartQty,
    showStepper: cartQty > 0 && requiresOptions === false,
    busy: checking || addToCart.isPending || updateItem.isPending || removeItem.isPending,
    onPlus,
    onMinus,
    cartAuthModal: cartAuth.modal,
  };
}

function AddControl({
  cartQty,
  showStepper,
  busy,
  onPlus,
  onMinus,
  size = "md",
}: {
  cartQty: number;
  showStepper: boolean;
  busy: boolean;
  onPlus: (e: React.MouseEvent) => void;
  onMinus: (e: React.MouseEvent) => void;
  size?: "sm" | "md";
}) {
  const dim = size === "sm" ? "h-7 w-7" : "h-8 w-8";
  const btn = size === "sm" ? "h-6 w-6" : "h-7 w-7";
  const ic = size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5";

  if (showStepper) {
    return (
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex items-center gap-0.5 rounded-full bg-primary px-1 py-1 shadow-md"
      >
        <button onClick={onMinus} disabled={busy} className={`flex ${btn} items-center justify-center rounded-full text-secondary transition hover:bg-black/10 disabled:opacity-50`} aria-label="Retirer">
          <Minus className={ic} strokeWidth={3} />
        </button>
        <span className="min-w-[18px] text-center text-sm font-bold text-secondary">{cartQty}</span>
        <button onClick={onPlus} disabled={busy} className={`flex ${btn} items-center justify-center rounded-full text-secondary transition hover:bg-black/10 disabled:opacity-50`} aria-label="Ajouter">
          <Plus className={ic} strokeWidth={3} />
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={onPlus}
      disabled={busy}
      className={`flex ${dim} items-center justify-center rounded-full shadow-md transition hover:scale-110 active:scale-95 disabled:opacity-50 ${cartQty > 0 ? "bg-primary" : "bg-white dark:bg-[#2A2A2A]"}`}
      aria-label="Ajouter"
    >
      {cartQty > 0 ? <span className="text-sm font-bold text-secondary">{cartQty}</span> : <Plus className="h-4 w-4" strokeWidth={2.5} />}
    </button>
  );
}



/* ══════════════════════════════════════════════════════════════ */
/*  STORE INFO MODAL                                             */
/* ══════════════════════════════════════════════════════════════ */

const DAY_NAMES = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];

const DELIVERY_TYPE_LABEL: Record<StoreConfig["delivery_type"], string> = {
  both: "À la demande et planifiée",
  delivery: "À la demande",
  // Returned by the API for 25 stores, all with accepts_scheduled_orders=false
  // — immediate delivery, no scheduling. Same promise as "delivery".
  on_demand: "À la demande",
  scheduled: "Planifiée",
  pickup: "À emporter",
};

/**
 * Fuseau d'Alger, fixé.
 *
 * Sans lui, une date prend le fuseau de la machine qui l'affiche : UTC sur le
 * serveur, UTC+1 chez le client. Un avis posté entre 23 h et minuit UTC tombait
 * un jour différent de chaque côté, et React rejetait le rendu serveur de la
 * fiche (erreur #418). Audit du 16/09/2026 : 25 fiches sur 520 en erreur, et
 * toutes affichaient un avis de 23 h UTC — aucune des 38 fiches saines testées.
 */
const REVIEW_TIME_ZONE = "Africa/Algiers";

function reviewDateShort(iso: string) {
  return new Date(iso).toLocaleDateString("fr-FR", { timeZone: REVIEW_TIME_ZONE });
}

function StoreInfoModal({
  store,
  config,
  onClose,
}: {
  store: StoreDetail;
  config: StoreConfig | undefined;
  onClose: () => void;
}) {
  const [descExpanded, setDescExpanded] = useState(false);
  const [filterRating, setFilterRating] = useState<number | undefined>(undefined);
  const [sortBy, setSortBy] = useState<"rating" | "created_at">("rating");
  const [selectedReview, setSelectedReview] = useState<StoreReview | null>(null);

  const dist = formatDistance(store.distance);

  const lat = store.location?.coordinates[1];
  const lng = store.location?.coordinates[0];
  const mapsLink = lat && lng
    ? `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
    : null;

  const { data: reviewsData } = useStoreReviews(store.ulid, {
    limit: 10,
    sortBy,
    sortOrder: "DESC",
    filterRating,
  });
  const reviews = reviewsData?.reviews ?? [];
  const avgRating = reviewsData?.average_rating ?? store.rating;
  const totalReviews = reviewsData?.total_reviews ?? store.reviews_count;
  const distribution = reviewsData?.rating_distribution ?? {};

  const paymentMethods = (config?.payment_methods ?? []).filter((m) => m.scope !== "b2b");

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 z-50 flex w-full flex-col bg-white dark:bg-[#1E1E1E] shadow-2xl sm:w-[440px]">
        {/* Header */}
        <div className="relative flex h-14 shrink-0 items-center justify-center border-b border-gray-100 dark:border-[#313131] px-4">
          <button
            onClick={onClose}
            aria-label="Retour"
            className="absolute left-4 flex h-9 w-9 items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-[#313131] transition text-gray-700 dark:text-white"
          >
            <ChevronLeft className="h-5 w-5" strokeWidth={2.5} />
          </button>
          <h2 className="text-base font-bold text-gray-900 dark:text-white">Détails du partenaire</h2>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Map (maplibre-gl chargé seulement ici, dans la modale d'informations) */}
          {lat && lng && (
            <div className="px-4 pt-4">
              <StoreLocationMap lat={lat} lng={lng} />
            </div>
          )}

          {/* Store summary */}
          <div className="px-5 pt-5 pb-4">
            <p className="text-2xl font-extrabold text-gray-900 dark:text-white leading-tight">{store.name}</p>
            <div className="mt-2 flex items-center justify-between gap-3">
              <div className="flex items-center gap-1.5 text-sm">
                {avgRating > 0 && (
                  <>
                    <Star className="h-4 w-4 text-amber-400" fill="currentColor" />
                    <span className="font-bold text-gray-900 dark:text-white">{Number(avgRating).toFixed(1)}</span>
                  </>
                )}
                {dist && <span className="text-gray-500 dark:text-gray-400">· {dist}</span>}
              </div>
              {mapsLink && (
                <a
                  href={mapsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex shrink-0 items-center gap-1.5 text-sm font-semibold text-secondary dark:text-primary"
                >
                  <MapIcon className="h-4 w-4" strokeWidth={2} />
                  Voir sur Maps
                </a>
              )}
            </div>
            {store.formatted_address && (
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{store.formatted_address}</p>
            )}
          </div>

          {/* Delivery type */}
          {config && (
            <div className="px-5 pb-2">
              <h3 className="mb-2 text-lg font-bold text-gray-900 dark:text-white">Type de livraison</h3>
              <div className="flex items-center gap-3 rounded-2xl border border-gray-200 dark:border-[#313131] px-4 py-3.5">
                <Package className="h-5 w-5 shrink-0 text-gray-500 dark:text-gray-400" strokeWidth={1.8} />
                <span className="text-sm font-semibold text-gray-900 dark:text-white">Type de livraison</span>
                <span className="ml-auto text-sm text-gray-500 dark:text-gray-400">{DELIVERY_TYPE_LABEL[config.delivery_type]}</span>
              </div>
            </div>
          )}

          {/* Payment methods */}
          {paymentMethods.length > 0 && (
            <div className="px-5 pt-3 pb-2">
              <h3 className="mb-2 text-lg font-bold text-gray-900 dark:text-white">Moyens de paiement</h3>
              <div className="overflow-hidden rounded-2xl border border-gray-200 dark:border-[#313131] divide-y divide-gray-100 dark:divide-[#313131]">
                {paymentMethods.map((m) => (
                  <div key={m.ulid} className="flex items-center gap-3 px-4 py-3.5">
                    <CreditCard className="h-5 w-5 shrink-0 text-gray-500 dark:text-gray-400" strokeWidth={1.8} />
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{m.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Opening hours */}
          {config && config.opening_hours.length > 0 && (
            <div className="px-5 pt-3 pb-2">
              <h3 className="mb-2 text-lg font-bold text-gray-900 dark:text-white">Heures d&apos;ouverture</h3>
              <div className="overflow-hidden rounded-2xl border border-gray-200 dark:border-[#313131] divide-y divide-gray-100 dark:divide-[#313131]">
                {config.opening_hours.map((h) => (
                  <div key={h.day_id} className="flex items-center justify-between px-4 py-3.5 text-sm">
                    <span className="font-medium text-gray-900 dark:text-white">{DAY_NAMES[h.day_id]}</span>
                    <span className="text-gray-500 dark:text-gray-400">{h.start_time} – {h.end_time}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reviews */}
          <div className="px-5 pt-4 pb-8">
            <h3 className="mb-3 text-lg font-bold text-gray-900 dark:text-white">
              Avis {totalReviews > 0 && `(${totalReviews})`}
            </h3>

            {/* Summary card */}
            <div className="flex items-center gap-5 rounded-2xl border border-gray-200 dark:border-[#313131] p-5">
              <div className="shrink-0 text-center">
                <p className="text-4xl font-extrabold text-gray-900 dark:text-white leading-none">{Number(avgRating).toFixed(1)}</p>
                <div className="mt-1.5 flex items-center justify-center gap-0.5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} fill="currentColor" className={`h-3.5 w-3.5 ${i < Math.round(avgRating) ? "text-amber-400" : "text-gray-200 dark:text-gray-700"}`} />
                  ))}
                </div>
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">{totalReviews} avis</p>
              </div>
              <div className="flex-1 space-y-1">
                {[5, 4, 3, 2, 1].map((s) => {
                  const count = distribution[String(s)] ?? 0;
                  const pct = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
                  return (
                    <div key={s} className="flex items-center gap-2">
                      <span className="w-5 text-xs text-gray-500 dark:text-gray-400">{s}.0</span>
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-[#313131]">
                        <div className="h-full rounded-full bg-amber-400" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-6 text-right text-xs text-gray-500 dark:text-gray-400">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Filter by rating */}
            <p className="mt-4 mb-2 text-sm text-gray-500 dark:text-gray-400">Filtrer par note</p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilterRating(undefined)}
                className={`rounded-full px-4 py-1.5 text-sm font-semibold transition ${
                  filterRating === undefined ? "bg-primary text-secondary" : "bg-gray-100 dark:bg-[#2A2A2A] text-gray-600 dark:text-gray-300"
                }`}
              >
                Tous
              </button>
              {[5, 4, 3, 2, 1].map((s) => (
                <button
                  key={s}
                  onClick={() => setFilterRating(filterRating === s ? undefined : s)}
                  className={`flex items-center gap-1 rounded-full px-3.5 py-1.5 text-sm font-semibold transition ${
                    filterRating === s ? "bg-primary text-secondary" : "bg-gray-100 dark:bg-[#2A2A2A] text-gray-600 dark:text-gray-300"
                  }`}
                >
                  {s} <Star fill="currentColor" className="h-3.5 w-3.5 text-amber-400" />
                </button>
              ))}
            </div>

            {/* Sort tabs */}
            <div className="mt-3 grid grid-cols-2 gap-3">
              <button
                onClick={() => setSortBy("rating")}
                className={`rounded-full py-2.5 text-sm font-bold transition ${
                  sortBy === "rating" ? "bg-primary text-secondary" : "bg-gray-100 dark:bg-[#2A2A2A] text-gray-700 dark:text-gray-300"
                }`}
              >
                Mieux notés
              </button>
              <button
                onClick={() => setSortBy("created_at")}
                className={`rounded-full py-2.5 text-sm font-bold transition ${
                  sortBy === "created_at" ? "bg-primary text-secondary" : "bg-gray-100 dark:bg-[#2A2A2A] text-gray-700 dark:text-gray-300"
                }`}
              >
                Plus récents
              </button>
            </div>

            {/* Review list */}
            <div className="mt-4 space-y-3">
              {reviews.length === 0 ? (
                <p className="rounded-2xl border border-gray-200 dark:border-[#313131] px-4 py-6 text-center text-sm text-gray-400 dark:text-gray-500">
                  Aucun avis pour cette sélection.
                </p>
              ) : (
                reviews.map((review) => (
                  <button
                    key={review.ulid}
                    onClick={() => setSelectedReview(review)}
                    className="w-full rounded-2xl border border-gray-200 dark:border-[#313131] p-4 text-left transition hover:border-gray-300 dark:hover:border-[#444]"
                  >
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-bold text-gray-900 dark:text-white">
                        {review.customer.firstname} {review.customer.lastname?.slice(0, 1)}.
                      </p>
                      {review.customer.orders_count > 0 && (
                        <span className="rounded-full bg-gray-100 dark:bg-[#2A2A2A] px-2 py-0.5 text-[11px] font-medium text-gray-500 dark:text-gray-400">
                          {review.customer.orders_count} commande{review.customer.orders_count > 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      <div className="flex items-center gap-0.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star key={i} fill="currentColor" className={`h-3.5 w-3.5 ${i < review.rating ? "text-amber-400" : "text-gray-200 dark:text-gray-700"}`} />
                        ))}
                      </div>
                      <span className="text-xs text-gray-400 dark:text-gray-500">{reviewDateShort(review.created_at)}</span>
                    </div>
                    <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">{review.customer.reviews_count} avis</p>
                    {review.comment && (
                      <p className="mt-2 text-sm leading-relaxed text-gray-700 dark:text-gray-300 line-clamp-3">{review.comment}</p>
                    )}
                    {review.order_items.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {review.order_items.slice(0, 3).map((item) => (
                          <span key={item.ulid} className="rounded-full bg-gray-50 dark:bg-[#2A2A2A] px-2.5 py-1 text-[11px] font-medium text-gray-600 dark:text-gray-400">
                            {item.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </button>
                ))
              )}
            </div>

            {store.description && (
              <div className="mt-6">
                <p className="mb-2 text-lg font-bold text-gray-900 dark:text-white">À propos</p>
                <p className={`text-sm text-gray-600 dark:text-gray-400 leading-relaxed ${descExpanded ? "" : "line-clamp-3"}`}>
                  {store.description}
                </p>
                <button
                  onClick={() => setDescExpanded((v) => !v)}
                  className="mt-1.5 text-sm font-semibold text-gray-900 dark:text-white hover:underline"
                >
                  {descExpanded ? "Voir moins" : "Voir plus"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedReview && (
        <ReviewDetailModal review={selectedReview} onClose={() => setSelectedReview(null)} />
      )}
    </>
  );
}



/* ══════════════════════════════════════════════════════════════ */
/*  Deals modal                                                  */
/* ══════════════════════════════════════════════════════════════ */

type DealColor = "red" | "green" | "amber";

interface Deal {
  id: string;
  label: string;
  sub: string;
  description?: string;
  color: DealColor;
}

/** Per-variant styling so every deal surface (row, grid, detail) stays in sync. */
const DEAL_STYLES: Record<DealColor, { bubble: string; bubbleLg: string; text: string }> = {
  red: { bubble: "bg-red-50", bubbleLg: "bg-red-50 dark:bg-red-950/30", text: "text-red-500" },
  green: { bubble: "bg-green-50", bubbleLg: "bg-green-50 dark:bg-green-950/30", text: "text-green-600" },
  amber: { bubble: "bg-amber-50", bubbleLg: "bg-amber-50 dark:bg-amber-950/30", text: "text-amber-600" },
};

function DealsAllModal({ deals, onClose, onSelectDeal }: {
  deals: Deal[];
  onClose: () => void;
  onSelectDeal: (deal: Deal) => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute w-max-7xl inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-1/4 rounded-2xl bg-white dark:bg-[#2A2A2A] shadow-2xl overflow-hidden">
        <div className="flex items-start justify-between px-6 pt-5 pb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Offres</h2>
            <p className="mt-0.5 text-[13px] text-gray-500 dark:text-gray-400">Appliquées à la caisse. Limitées à 1 par commande. Conditions applicables.</p>
          </div>
          <button onClick={onClose} className="ml-3 flex h-8 w-8 shrink-0 items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-[#313131] transition">
            <X className="h-4 w-4 text-gray-600 dark:text-gray-400" strokeWidth={2.5} />
          </button>
        </div>
        <div className="divide-y divide-gray-100 dark:divide-[#313131]">
          {deals.map((deal) => (
            <button
              key={deal.id}
              onClick={() => onSelectDeal(deal)}
              className="flex w-full items-center gap-4 px-6 py-4 text-left hover:bg-gray-50 dark:hover:bg-[#313131] transition"
            >
              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${DEAL_STYLES[deal.color].bubble}`}>
                <DealIcon color={deal.color} />
              </span>
              <div className="min-w-0 flex-1">
                <p className={`text-sm font-bold ${DEAL_STYLES[deal.color].text}`}>{deal.label}</p>
                {deal.sub && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{deal.sub}</p>}
              </div>
              <ChevronRight className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" strokeWidth={2} />
            </button>
          ))}
        </div>
        <div className="p-4 border-t border-gray-100 dark:border-[#313131]">
          <button onClick={onClose} className="w-full rounded-full bg-gray-900 py-2.5 text-sm font-bold text-white hover:bg-primary hover:text-secondary transition">
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

function DealDetailModal({ deal, onClose }: { deal: Deal; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 sm:items-center sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full rounded-t-2xl bg-white dark:bg-[#2A2A2A] shadow-2xl sm:max-w-sm sm:rounded-2xl">
        {/* Drag handle (mobile) */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden">
          <div className="h-1 w-10 rounded-full bg-gray-300 dark:bg-[#444]" />
        </div>
        <button
          onClick={onClose}
          className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full text-gray-500 transition hover:bg-gray-100 dark:hover:bg-[#313131]"
          aria-label="Fermer"
        >
          <X className="h-5 w-5" strokeWidth={2} />
        </button>

        <div className="px-6 pb-2 pt-4 text-center">
          <span className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full ${DEAL_STYLES[deal.color].bubbleLg}`}>
            {deal.color === "green" ? (
              <Truck className="h-7 w-7 text-green-600" strokeWidth={2} />
            ) : deal.color === "amber" ? (
              <Sparkles className="h-7 w-7 text-amber-500" strokeWidth={2} />
            ) : (
              <Tag className="h-7 w-7 text-red-500" fill="currentColor" strokeWidth={2} />
            )}
          </span>
          <h2 className={`text-2xl font-extrabold ${DEAL_STYLES[deal.color].text}`}>{deal.label}</h2>
          {deal.sub && <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{deal.sub}</p>}
          {deal.description && <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{deal.description}</p>}
        </div>

        <div className="px-6 pb-6 pt-3">
          <button onClick={onClose} className="w-full rounded-full bg-primary py-3.5 text-sm font-bold text-secondary transition hover:opacity-90">
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

function DealIcon({ color }: { color: DealColor }) {
  if (color === "green") return <Truck className="h-4 w-4 text-green-600" strokeWidth={2} />;
  if (color === "amber") return <Sparkles className="h-4 w-4 text-amber-500" strokeWidth={2} />;
  return <Tag className="h-4 w-4 text-red-500" fill="currentColor" strokeWidth={2} />;
}

/* ══════════════════════════════════════════════════════════════ */
/*  PAGE                                                         */
/* ══════════════════════════════════════════════════════════════ */

export function StoreView({
  storeId,
  storeGeo,
  initialStore,
}: {
  storeId: string;
  // Store's own coordinates (from the server) so the guest detail fetch isn't
  // geo-locked to the visitor's location and works for any zone.
  storeGeo?: { latitude: number; longitude: number } | null;
  // Server-fetched store → seeds the query so content renders in the SSR HTML.
  initialStore?: StoreDetail;
}) {
  const router = useRouter();
  const { data: store, isLoading: storeLoading, isError: storeError } = useStoreDetail(
    storeId,
    storeGeo,
    initialStore,
  );
  const { data: config } = useStoreConfig(storeId);
  const { data: categories } = useStoreMenuCategories(storeId);
  // Best-rated first, so the strongest reviews lead the store page. Params must
  // match the server prefetch in page.tsx (STORE_REVIEW_PARAMS) or the hydrated
  // cache is missed and the reviews never appear in the server-rendered HTML.
  const { data: reviewsData } = useStoreReviews(storeId, { limit: 6, sortBy: "rating", sortOrder: "DESC" });

  // The catalogue endpoints return reviews_count = 0 for every store, so the
  // header showed "0 avis" next to a rating. The reviews endpoint is the only
  // source with real totals — and it must be the one displayed, because
  // Google requires the rating in aggregateRating to match the visible one.
  const shownRating = reviewsData?.average_rating || store?.rating || 0;
  const shownReviewCount = reviewsData?.total_reviews ?? store?.reviews_count ?? 0;
  const shownReviewLabel =
    shownReviewCount >= 1000 ? `${(shownReviewCount / 1000).toFixed(0)}k` : `${shownReviewCount}`;
  const { data: storeCart } = useStoreCart(storeId);
  const cartItemCount = storeCart?.items?.reduce((sum, i) => sum + i.quantity, 0) ?? 0;
  const cartSubtotal = storeCart?.subtotal ?? 0;

  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showDealsModal, setShowDealsModal] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  // Reuse the navbar's global search so that, on a store page, the navbar
  // searches this store's products instead of filtering home's store list.
  const searchInput = useSearchStore((s) => s.query);
  const setSearchInput = useSearchStore((s) => s.setQuery);
  const [searchQuery, setSearchQuery] = useState("");

  // Start each store with an empty search box, and clear it on leave so the
  // term doesn't bleed back into the home store filter.
  useEffect(() => {
    setSearchInput("");
    return () => setSearchInput("");
  }, [setSearchInput]);
  const [selectedProduct, setSelectedProduct] = useState<MenuProduct | null>(null);
  const [viewingCategory, setViewingCategory] = useState<MenuCategory | null>(null);
  const [viewingAllCats, setViewingAllCats] = useState(false);

  // Mobile: collapsing hero header + category tab sheet
  // Keep the skeleton up for a brief minimum so a refresh is visible/felt — but
  // ONLY when we start without data. When the store is already hydrated (server
  // render / prefetched cache), skip it so the real content (h1, address, menu)
  // is in the server HTML for SEO instead of a skeleton.
  const [minLoading, setMinLoading] = useState(!store);
  useEffect(() => {
    if (!minLoading) return;
    const t = setTimeout(() => setMinLoading(false), 1500);
    return () => clearTimeout(t);
  }, [minLoading]);

  const [collapsed, setCollapsed] = useState(false);
  const [showCatSheet, setShowCatSheet] = useState(false);
  const collapseSentinelRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const mobileSearchRef = useRef<HTMLInputElement>(null);

  const focusMobileSearch = useCallback(() => {
    mobileSearchRef.current?.scrollIntoView({ block: "center" });
    mobileSearchRef.current?.focus();
  }, []);

  // Le titre vient des métadonnées du serveur (note, avis, commune). Il était
  // réécrit ici en « Nom — Fast » après hydratation — et c'est le DOM rendu que
  // Google lit : le titre travaillé ne survivait pas au JavaScript.

  /*
   * Aucune redirection sur erreur.
   *
   * Il y avait ici `if (storeError) router.replace("/home")`. Or une relecture
   * en arrière-plan qui échoue met `isError` à vrai MÊME quand le serveur a déjà
   * fourni le magasin (React Query garde les données et passe en « error »). Et
   * le rendu de Google ne peut pas appeler l'API : `robots.txt` interdit `/api/`.
   *
   * Reproduit avec `/api/` bloqué : chaque fiche devenait `/home` en quelques
   * secondes — URL, canonique et h1 de l'accueil. Search Console, 15/09 : sur les
   * fiches explorées, 40 vues comme « Page avec redirection » et 18 avec une
   * canonique `/home`. Un visiteur dont la requête échouait (hors zone, réseau)
   * était renvoyé à l'accueil de la même façon.
   *
   * Le serveur tranche déjà l'existence (jamais de 404 pour un ULID valide) : ce
   * composant garde ce qu'il a, et n'affiche un message que s'il n'a rien.
   */

  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: searchResults, isFetching: searchFetching } = useStoreMenuSearch(storeId, searchQuery);
  const sectionRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const dealsScrollRef = useRef<HTMLDivElement>(null);
  const scrollDeals = (dir: "left" | "right") => {
    dealsScrollRef.current?.scrollBy({ left: dir === "left" ? -300 : 300, behavior: "smooth" });
  };

  // Use the server-seeded auth flag (not the zustand user) for anything rendered
  // here, so SSR and the client's first render agree (no hydration mismatch).
  const { authed } = useAppEnv();
  const cartAuth = useCartAuthGate();
  const { toggle, isPending } = useToggleFavorite();

  useEffect(() => {
    if (categories?.length && !activeCategory) {
      const id = requestAnimationFrame(() => setActiveCategory(categories[0].ulid));
      return () => cancelAnimationFrame(id);
    }
  }, [categories, activeCategory]);

  const scrollToSection = useCallback((id: string) => {
    setActiveCategory(id);
    sectionRefs.current.get(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const categoriesCount = categories?.length ?? 0;
  const hasReviews = (reviewsData?.total_reviews ?? 0) > 0;

  useEffect(() => {
    if (!categoriesCount && !hasReviews) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveCategory(entry.target.getAttribute("data-cat-id"));
          }
        }
      },
      { rootMargin: "-120px 0px -60% 0px", threshold: 0 }
    );
    for (const [, el] of sectionRefs.current) observer.observe(el);
    return () => observer.disconnect();
  }, [categoriesCount, hasReviews]);

  // Mobile: collapse the hero header once it scrolls under the sticky bar.
  // Depends on the loading flags so it re-runs once the skeleton is replaced
  // by the real content — otherwise the sentinel isn't mounted yet on first
  // run and the observer never attaches (header stays transparent, no title).
  useEffect(() => {
    const el = collapseSentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => setCollapsed(!entry.isIntersecting),
      { rootMargin: "-56px 0px 0px 0px", threshold: 0 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [storeLoading, minLoading]);

  // Mobile: keep the active category tab in view within the sticky tab bar.
  useEffect(() => {
    if (!activeCategory) return;
    const c = tabsRef.current;
    const btn = c?.querySelector<HTMLElement>(`[data-tab="${activeCategory}"]`);
    if (c && btn) {
      c.scrollTo({ left: btn.offsetLeft - c.clientWidth / 2 + btn.clientWidth / 2, behavior: "smooth" });
    }
  }, [activeCategory]);

  // Only show the skeleton while we genuinely have no data — when the store is
  // already hydrated (server render / prefetch), render real content so the h1,
  // address and description are in the server HTML for SEO.
  if (!store && (storeLoading || minLoading)) return <StoreDetailSkeleton />;
  if (!store) {
    return (
      <div className="flex h-[60vh] flex-col items-center justify-center gap-3 px-4 text-center">
        <p className="text-gray-600 dark:text-gray-300">
          {storeError
            ? "Impossible d'afficher ce magasin pour le moment."
            : "Magasin introuvable"}
        </p>
        <Link
          href="/home"
          className="rounded-full border border-gray-200 px-4 py-2 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-50 dark:border-[#313131] dark:text-white dark:hover:bg-[#262626]"
        >
          Voir les magasins près de chez vous
        </Link>
      </div>
    );
  }

  const isOpen = store.availability === "open";
  const dist = formatDistance(store.distance);
  const delivery = deliveryDisplay(store);
  const deliveryFree = delivery.kind === "free";
  // Allow-listing the delivery types silently hid the fee and the ETA for the
  // 25 stores the API returns as "on_demand" — a value this code never knew
  // about. Only "pickup" actually excludes delivery, so exclude that one and
  // let every other value (including ones added later) show the block.
  const canDelivery = config?.delivery_type !== "pickup";

  const deals: Deal[] = [];
  if (store.discount) {
    const label = store.discount.value_type === "percentage"
      ? `-${store.discount.value}% de réduction`
      : `-${store.discount.value} DA`;
    const sub = store.discount.min_order_cart != null && store.discount.min_order_cart > 0
      ? `Commande min. ${formatAmount(store.discount.min_order_cart)} DA`
      : store.discount.description ?? "Sur votre commande";
    deals.push({ id: "discount", label, sub, description: store.discount.description, color: "red" });
  }
  if (deliveryFree) {
    deals.push({
      id: "delivery",
      label: "Livraison gratuite",
      sub: `Au lieu de ${formatAmount(delivery.kind === "free" ? delivery.was : store.delivery_charge)} DA`,
      color: "green",
    });
  }
  // Store tags ("Offre spéciale", "En exclusivité"…) are promotions too — show
  // them as proper cards in "Offres & avantages" rather than as a small badge
  // tucked into the sidebar.
  for (const tag of store.tags ?? []) {
    deals.push({ id: `tag-${tag}`, label: tag, sub: "", color: "green" });
  }

  const isGrocery = store.template === "grocery";

  // Categories to surface in the tabs/sheet — drop flat categories that have
  // no products (lazy categories keep showing until their products resolve).
  const menuCategories = (categories ?? []).filter(
    (c) => !Array.isArray(c.products) || c.products.length > 0
  );

  return (
    <div className="min-h-screen bg-white dark:bg-[#1E1E1E]">
      {showInfoModal && (
        <StoreInfoModal store={store} config={config} onClose={() => setShowInfoModal(false)} />
      )}
      {showDealsModal && (
        <DealsAllModal deals={deals} onClose={() => setShowDealsModal(false)} onSelectDeal={(d) => setSelectedDeal(d)} />
      )}
      {selectedDeal && (
        <DealDetailModal deal={selectedDeal} onClose={() => setSelectedDeal(null)} />
      )}
  
      {/* ═══ MOBILE collapsing header ═══ */}
      <header className={`md:hidden sticky top-0 z-40 flex h-14 items-center gap-2 px-3 transition-colors duration-200 ${collapsed ? "bg-white dark:bg-[#1E1E1E] border-b border-gray-100 dark:border-[#313131] shadow-sm" : "bg-transparent"}`}>
        <button
          onClick={() => router.back()}
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition ${collapsed ? "text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-[#313131]" : "bg-white/90 dark:bg-[#2A2A2A]/90 text-gray-900 dark:text-white shadow-sm backdrop-blur-sm"}`}
          aria-label="Fermer"
        >
          <X className="h-5 w-5" strokeWidth={2} />
        </button>

        <p className={`min-w-0 flex-1 truncate text-center text-base font-bold text-gray-900 dark:text-white transition-opacity duration-200 ${collapsed ? "opacity-100" : "opacity-0"}`}>
          {store.name}
        </p>

        {collapsed ? (
          <button
            onClick={focusMobileSearch}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-[#313131] transition"
            aria-label="Rechercher"
          >
            <Search className="h-5 w-5" strokeWidth={2} />
          </button>
        ) : (
          <div className="flex shrink-0 items-center gap-0.5 rounded-full bg-white/90 dark:bg-[#2A2A2A]/90 px-1.5 py-1 shadow-sm backdrop-blur-sm">
            {authed && (
              <button
                onClick={() => toggle(store.ulid, store.is_favorite)}
                disabled={isPending}
                className="flex h-7 w-7 items-center justify-center rounded-full transition hover:bg-black/5 disabled:opacity-50"
                aria-label={store.is_favorite ? "Retirer des favoris" : "Enregistrer"}
              >
                <Heart className="h-[18px] w-[18px]" strokeWidth={2} fill={store.is_favorite ? "currentColor" : "none"} style={store.is_favorite ? { fill: "#ef4444", color: "#ef4444" } : undefined} />
              </button>
            )}
            <button
              onClick={() => setShowInfoModal(true)}
              className="flex h-7 w-7 items-center justify-center rounded-full text-gray-900 dark:text-white transition hover:bg-black/5"
              aria-label="Informations"
            >
              <Info className="h-[18px] w-[18px]" strokeWidth={2} />
            </button>
          </div>
        )}
      </header>

      {/* Single document heading. The name is displayed visually in the hero
          (mobile) and the sidebar (desktop) — those are two responsive halves
          of one title, so neither can be the <h1> without the other becoming a
          display:none heading. This one is always in the DOM. */}
      <h1 className="sr-only">{store.name}</h1>

      <div className="mx-auto px-4 md:px-6 lg:px-8">
        {/* ═══ HERO ═══ */}
        <div className="relative -mx-4 -mt-14 h-[300px] overflow-hidden bg-gray-200 dark:bg-[#313131] md:mx-0 md:mt-2 md:h-[260px] md:rounded-2xl">
          {store.image && (
            <Image
              src={store.image}
              alt={store.name}
              fill
              className="object-cover"
              sizes="(max-width: 1280px) 100vw, 1280px"
              priority
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-black/20 md:from-black/30 md:via-transparent" />

          {/* Mobile: store name overlay */}
          <div className="md:hidden absolute bottom-4 left-4 right-4 z-10 text-white">
            <p className="text-[26px] font-extrabold leading-tight drop-shadow-md">{store.name}</p>
            <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm font-medium drop-shadow">
              <Star fill="currentColor" className="h-4 w-4 text-white" />
              <span className="font-semibold">{shownRating.toFixed(1)}</span>
              {shownReviewCount > 0 && (
                <span className="underline underline-offset-2">{shownReviewLabel} avis</span>
              )}
              {store.formatted_address && (
                <>
                  <span>·</span>
                  <span className="truncate">{store.formatted_address}</span>
                </>
              )}
            </div>
          </div>

          {/* Desktop buttons (mobile uses the sticky header) */}
          <div className="hidden md:flex absolute top-4 right-4 z-10 items-center gap-2">
            {authed && (
              <button
                onClick={() => toggle(store.ulid, store.is_favorite)}
                disabled={isPending}
                className="flex items-center gap-1.5 rounded-full bg-white/95 dark:bg-[#2A2A2A]/95 px-4 py-2 text-sm font-semibold shadow-lg backdrop-blur-sm transition hover:bg-white dark:hover:bg-[#2A2A2A] disabled:opacity-50"
              >
                <Heart className="h-[18px] w-[18px]" strokeWidth={2} fill={store.is_favorite ? "currentColor" : "none"} style={store.is_favorite ? { fill: "#ef4444", color: "#ef4444" } : undefined} />
                {store.is_favorite ? "Retirer des favoris" : "Enregistrer"}
              </button>
            )}
          </div>

          <button
            onClick={() => router.back()}
            className="hidden md:flex absolute top-4 left-4 z-10 h-9 w-9 items-center justify-center rounded-full bg-white/95 dark:bg-[#2A2A2A]/95 shadow-lg backdrop-blur-sm transition hover:bg-white dark:hover:bg-[#2A2A2A]"
            aria-label="Retour"
          >
            <ChevronLeft className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>

        {/* Sentinel — toggles the collapsed mobile header */}
        <div ref={collapseSentinelRef} className="md:hidden h-px w-full" aria-hidden />

        {/* Category icon bar — grocery only */}
        {isGrocery && categories && categories.length > 0 && (
          <CategoryIconBar
            categories={categories}
            activeCategory={activeCategory}
            onSelect={(id) => {
              setViewingCategory(null);
              setViewingAllCats(false);
              scrollToSection(id);
            }}
            onViewAll={() => { setViewingAllCats(true); setViewingCategory(null); }}
          />
        )}

        {/* ═══ SIDEBAR + MAIN ═══ */}
        <div className="relative flex gap-0">
          {/* ── SIDEBAR ── */}
          <aside className="hidden md:flex flex-col shrink-0 border-r border-gray-100 dark:border-[#313131] w-[240px] pt-6 pr-6 sticky top-16 self-start max-h-[calc(100vh-4rem)] overflow-y-auto scrollbar-hide">
            {/* Store info */}
                                    <div className="min-w-0 py-2 px-1 flex-1">
            <div className="flex items-center gap-3">
              <p className="text-[26px] font-extrabold leading-tight text-gray-900 dark:text-white tracking-tight">
                {store.name}
              </p>
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-600 dark:text-gray-400">
              <span className="font-semibold">{shownRating.toFixed(1)}</span>
              <Star fill="currentColor" className="h-3.5 w-3.5 shrink-0 text-yellow-400" />
              {shownReviewCount > 0 && (
                <span className="text-gray-400 dark:text-gray-500">({shownReviewLabel} avis)</span>
              )}
              {dist && (
                <>
                  <span className="text-gray-300 dark:text-gray-400">·</span>
                  <span>{dist}</span>
                </>
              )}
            </div>
            {store.appCategories.length > 0 && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {store.appCategories.map((c) => c.name).join(" · ")}
              </p>
            )}
          </div>
            <div className="py-2 px-1 flex-shrink-0">

              <h2 className="text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">Infos du magasin</h2>

              <div className="mt-3 flex items-center gap-1.5 text-sm">
                <span className={`inline-block h-2 w-2 rounded-full ${isOpen ? "bg-green-500" : "bg-red-500"}`} />
                <span className={`font-medium ${isOpen ? "text-green-600" : "text-red-500"}`}>
                  {isOpen ? "Ouvert maintenant" : "Fermé"}
                </span>
              </div>

        <div className="mt-3">
  <div className="flex items-center justify-center gap-3 rounded-lg border border-gray-200 dark:border-[#313131] px-3 py-2">
    <span className="text-sm font-medium text-gray-900 dark:text-white">
      {delivery.kind === "free"
        ? "0 DA"
        : delivery.kind === "unknown"
          ? "Selon la distance"
          : `${formatAmount(delivery.kind === "discounted" ? delivery.price : delivery.price)} DA`}
    </span>

    {store.avg_time > 0 && (
      <>
        <span className="text-gray-300 dark:text-gray-600">•</span>
        <span className="text-sm font-medium text-gray-900 dark:text-white">
          {store.avg_time} min
        </span>
      </>
    )}
  </div>
</div>

              <button
                onClick={() => setShowInfoModal(true)}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 dark:border-[#313131] py-2 text-sm font-semibold text-gray-700 dark:text-gray-400 transition hover:bg-gray-50 dark:hover:bg-[#313131]"
              >
                Voir plus
              </button>

              {config && config.opening_hours.length > 0 && (
                <div className="mt-5 border-t border-gray-100 dark:border-[#313131] pt-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">Menu complet</h3>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    {config.opening_hours[0]?.start_time} - {config.opening_hours[0]?.end_time}
                  </p>
                </div>
              )}
            </div>

            {/* Category nav */}
            {(hasReviews || (categories && categories.length > 0)) && (
              <nav className="border-t border-gray-100 px-1 dark:border-[#313131] py-3">
                <div className="space-y-0.5">
                  {hasReviews && (
                    <button
                      onClick={() => scrollToSection("reviews")}
                      className={`flex w-full items-center overflow-hidden px-3 py-2 text-[13px] transition ${activeCategory === "reviews"
                          ? "border-l-[3px] border-gray-900 dark:border-white font-bold text-gray-900 dark:text-white"
                          : "border-l-[3px] border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                        }`}
                    >
                      <span className="truncate">Avis</span>
                    </button>
                  )}
                  {categories?.map((cat) => (
                    <button
                      key={cat.ulid}
                      onClick={() => scrollToSection(cat.ulid)}
                      title={cat.name}
                      className={`flex w-full items-center overflow-hidden px-3 py-2 text-[13px] transition ${activeCategory === cat.ulid
                          ? "border-l-[3px] border-gray-900 dark:border-white font-bold text-gray-900 dark:text-white"
                          : "border-l-[3px] border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                        }`}
                    >
                      <span className="truncate">{cat.name}</span>
                    </button>
                  ))}
                </div>
              </nav>
            )}
          </aside>

          {/* ── MAIN CONTENT ── */}
          <main className="min-w-0 flex-1 py-4 md:pl-6">
            {/* Mobile search */}
            <div className="relative md:hidden mb-4">
              <div className="absolute left-3.5 top-1/2 -translate-y-1/2">
                <Search className="h-4 w-4 text-gray-400 dark:text-white" />
              </div>
              <input
                ref={mobileSearchRef}
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={`Rechercher dans ${store.name}`}
                className="w-full rounded-full border border-gray-200 dark:border-[#313131] bg-gray-50 dark:bg-[#2A2A2A] py-2.5 pl-10 pr-4 text-sm placeholder-gray-400 dark:placeholder-gray-600 focus:border-gray-300 focus:outline-none scroll-mt-20"
              />
            </div>

            {/* Delivery info — mobile only (shown in the sidebar on desktop) */}
            {canDelivery && (
              <div className={`md:hidden grid gap-3 mb-2 ${store.avg_time > 0 ? "grid-cols-2" : "grid-cols-1"}`}>
                <div className="rounded-xl border border-gray-200 dark:border-[#313131] px-5 py-4 text-center">
                  <p className="text-base font-bold text-gray-900 dark:text-white">
                    {delivery.kind === "free"
                      ? "0 DA"
                      : delivery.kind === "unknown"
                        ? "Selon la distance"
                        : `${formatAmount(delivery.price)} DA`}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Frais de livraison</p>
                </div>
                {store.avg_time > 0 && (
                  <div className="rounded-xl border border-gray-200 dark:border-[#313131] px-5 py-4 text-center">
                    <p className="text-base font-bold text-gray-900 dark:text-white">{store.avg_time} min</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Temps de livraison</p>
                  </div>
                )}
              </div>
            )}

            {/* ── Deals & benefits ── */}
            {deals.length > 0 && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-[17px] font-bold text-gray-900 dark:text-white">Offres &amp; avantages</h2>
                  {/* Arrows + Voir tout only when there are more offers than fit (3+) */}
                  {deals.length > 3 && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setShowDealsModal(true)}
                        className="text-sm font-semibold text-gray-700 dark:text-gray-400 hover:text-black dark:hover:text-white transition"
                      >
                        Voir tout
                      </button>
                      <div className="flex gap-1">
                        <button onClick={() => scrollDeals("left")} className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 dark:border-[#313131] bg-white dark:bg-[#2A2A2A] hover:bg-gray-50 dark:hover:bg-[#313131] transition shadow-sm" aria-label="Précédent">
                          <ChevronLeft className="h-4 w-4" strokeWidth={2} />
                        </button>
                        <button onClick={() => scrollDeals("right")} className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 dark:border-[#313131] bg-white dark:bg-[#2A2A2A] hover:bg-gray-50 dark:hover:bg-[#313131] transition shadow-sm" aria-label="Suivant">
                          <ChevronRight className="h-4 w-4" strokeWidth={2} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div ref={dealsScrollRef} className="flex gap-3 overflow-x-auto grid grid-cols-3 scrollbar-hide pb-1">
                  {deals.map((deal) => (
                    <div
                      key={deal.id}
                      className="flex flex-1 min-w-[260px] items-center gap-3 rounded-xl border border-gray-200 dark:border-[#313131] px-4 py-3.5 text-left"
                    >
                      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${deal.color === "red" ? "bg-red-50" : "bg-green-50"}`}>
                        <DealIcon color={deal.color} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm font-bold ${DEAL_STYLES[deal.color].text}`}>{deal.label}</p>
                        {deal.sub && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{deal.sub}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Search results ── */}
            {searchQuery ? (
              <div className="mt-6">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  {searchFetching
                    ? "Recherche…"
                    : searchResults && searchResults.length > 0
                      ? `${searchResults.length} résultat${searchResults.length > 1 ? "s" : ""} pour « ${searchQuery} »`
                      : `Aucun résultat pour « ${searchQuery} »`}
                </p>
                {searchFetching && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 animate-pulse">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="flex gap-4 rounded-xl border border-gray-100 dark:border-[#313131] p-4">
                        <div className="flex-1 space-y-2">
                          <div className="h-4 w-3/4 rounded bg-gray-200 dark:bg-[#313131]" />
                          <div className="h-3 w-full rounded bg-gray-100 dark:bg-[#313131]" />
                          <div className="h-3 w-1/3 rounded bg-gray-100 dark:bg-[#313131]" />
                        </div>
                        <div className="h-[110px] w-[128px] shrink-0 rounded-xl bg-gray-200 dark:bg-[#313131]" />
                      </div>
                    ))}
                  </div>
                )}
                {!searchFetching && searchResults && searchResults.length > 0 && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {searchResults.map((product) => (
                      <ProductCard key={product.ulid} product={product} storeUlid={storeId} onProductClick={setSelectedProduct} />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* ── Reviews ── */}
                {reviewsData && reviewsData.total_reviews > 0 && (
                  <div
                    ref={(el) => { if (el) sectionRefs.current.set("reviews", el); }}
                    data-cat-id="reviews"
                  >
                    <ReviewsSection
                      storeSlug={storeSegment(store.name, storeId)}
                      averageRating={reviewsData.average_rating}
                      totalReviews={reviewsData.total_reviews}
                      reviews={reviewsData.reviews}
                    />
                  </div>
                )}

                {/* ── Mobile: sticky category tabs (scroll-spy) ── */}
                {!isGrocery && menuCategories.length > 0 && !selectedProduct && (
                  <div className="md:hidden sticky top-14 z-30 -mx-4 mb-3 flex items-center gap-1 border-b border-gray-100 dark:border-[#313131] bg-white dark:bg-[#1E1E1E] px-1">
                    <button
                      onClick={() => setShowCatSheet(true)}
                      className="flex h-11 w-9 shrink-0 items-center justify-center text-gray-700 dark:text-gray-300"
                      aria-label="Toutes les catégories"
                    >
                      <Menu className="h-5 w-5" strokeWidth={2} />
                    </button>
                    <div ref={tabsRef} className="flex flex-1 items-center gap-1 overflow-x-auto scrollbar-hide">
                      {menuCategories.map((cat) => {
                        const active = activeCategory === cat.ulid;
                        return (
                          <button
                            key={cat.ulid}
                            data-tab={cat.ulid}
                            onClick={() => scrollToSection(cat.ulid)}
                            className={`relative shrink-0 whitespace-nowrap px-3 py-2.5 text-[15px] transition ${active ? "font-bold text-gray-900 dark:text-white" : "font-medium text-gray-500 dark:text-gray-400"}`}
                          >
                            {cat.name}
                            <span className={`absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-secondary dark:bg-primary transition-opacity ${active ? "opacity-100" : "opacity-0"}`} />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ── Menu sections ── */}
                {categories && categories.length > 0 && (
                  <div className="mt-4">
                    {isGrocery && viewingAllCats ? (
                      <GroceryBrowseAllView
                        categories={categories}
                        storeName={store.name}
                        onSelectCategory={(cat) => { setViewingCategory(cat); setViewingAllCats(false); }}
                        onBack={() => setViewingAllCats(false)}
                      />
                    ) : isGrocery && viewingCategory ? (
                      <GroceryCategoryView
                        key={viewingCategory.ulid}
                        category={viewingCategory}
                        storeUlid={storeId}
                        storeName={store.name}
                        onProductClick={setSelectedProduct}
                        onBack={() => setViewingCategory(null)}
                        onBrowseAll={() => { setViewingAllCats(true); setViewingCategory(null); }}
                        onSelectCategory={(c) => setViewingCategory(c)}
                      />
                    ) : (
                      <>
                        {isGrocery ? (
                          categories.map((cat) => (
                            <GroceryCarouselSection
                              key={cat.ulid}
                              category={cat}
                              storeUlid={storeId}
                              onProductClick={setSelectedProduct}
                              onSelectCategory={(c) => { setViewingCategory(c); setViewingAllCats(false); }}
                              ref={(el) => { if (el) sectionRefs.current.set(cat.ulid, el); }}
                            />
                          ))
                        ) : (
                          <NonGroceryMenuBlock
                            categories={categories}
                            storeUlid={storeId}
                            storeName={store.name}
                            onProductClick={setSelectedProduct}
                            sectionRefs={sectionRefs}
                          />
                        )}
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>

      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          storeUlid={storeId}
          onClose={() => setSelectedProduct(null)}
        />
      )}

      {cartAuth.modal}

      {/* Mobile: category list sheet (hamburger) */}
      {showCatSheet && (
        <div
          className="md:hidden fixed inset-0 z-[70] flex flex-col bg-black/40"
          onClick={(e) => { if (e.target === e.currentTarget) setShowCatSheet(false); }}
        >
          <div className="mt-auto max-h-[70vh] overflow-y-auto rounded-t-2xl bg-white dark:bg-[#2A2A2A]">
            <div className="sticky top-0 flex items-center justify-between border-b border-gray-100 dark:border-[#313131] bg-white dark:bg-[#2A2A2A] px-4 py-3">
              <span className="text-base font-bold text-gray-900 dark:text-white">Catégories</span>
              <button
                onClick={() => setShowCatSheet(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 dark:hover:bg-[#313131] transition"
                aria-label="Fermer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="py-1">
              {menuCategories.map((cat) => (
                <button
                  key={cat.ulid}
                  onClick={() => { setShowCatSheet(false); scrollToSection(cat.ulid); }}
                  className={`flex w-full items-center justify-between px-5 py-3 text-left text-[15px] transition hover:bg-gray-50 dark:hover:bg-[#313131] ${activeCategory === cat.ulid ? "font-bold text-gray-900 dark:text-white" : "text-gray-700 dark:text-gray-300"}`}
                >
                  {cat.name}
                  {activeCategory === cat.ulid && <Check className="h-4 w-4 text-secondary dark:text-primary" strokeWidth={2.5} />}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Mobile: sticky "Check cart" bar when this store has an active cart */}
      {cartItemCount > 0 && (
        <div className="md:hidden fixed inset-x-0 bottom-0 z-40 px-4 pb-4 pt-2">
          <button
            onClick={() => router.push(`/cart/${storeId}`)}
            className="flex w-full items-center justify-between gap-3 rounded-full bg-primary px-3 py-3 shadow-lg transition hover:opacity-95"
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-secondary/30 text-sm font-bold text-secondary">
              {cartItemCount}
            </span>
            <span className="text-base font-bold text-secondary">Voir le panier</span>
            <span className="pr-1 text-base font-bold text-secondary">{formatAmount(cartSubtotal)} DA</span>
          </button>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  REVIEWS SECTION                                              */
/* ══════════════════════════════════════════════════════════════ */

function formatReviewDate(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  // Pinned locale, not the runtime default: this date is server-rendered, and
  // `undefined` resolves to the server's locale (en-US) while a French browser
  // would render it in French on hydration — a mismatch React has to repair,
  // on a page whose <html lang> is fr.
  return d.toLocaleDateString("fr-DZ", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: REVIEW_TIME_ZONE,
  });
}


function ReviewsSection({
  storeSlug,
  averageRating,
  totalReviews,
  reviews,
}: {
  /** Canonical path segment ({slug}--{ulid}), not the bare ULID: linking to the
   *  ULID form sends crawlers to a URL that only canonicalises elsewhere. */
  storeSlug: string;
  averageRating: number;
  totalReviews: number;
  reviews: StoreReview[];
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [selectedReview, setSelectedReview] = useState<StoreReview | null>(null);
  const scroll = (dir: "left" | "right") => {
    scrollRef.current?.scrollBy({ left: dir === "left" ? -280 : 280, behavior: "smooth" });
  };

  const showArrows = reviews.length >= 3;

  return (
    <>
      <div className="mt-5">
        <div className="flex items-center justify-between">
          <h2 className="text-[17px] font-bold text-gray-900 dark:text-white">Avis</h2>
          <div className="flex items-center gap-2">
            {showArrows && (
              <>
                <button onClick={() => scroll("left")} className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 dark:border-[#313131] text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#313131] transition">
                  <ChevronLeft className="h-4 w-4" strokeWidth={2} />
                </button>
                <button onClick={() => scroll("right")} className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 dark:border-[#313131] text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#313131] transition">
                  <ChevronRight className="h-4 w-4" strokeWidth={2} />
                </button>
              </>
            )}
            <Link href={`/stores/${storeSlug}/reviews`} className="rounded-full border border-gray-200 dark:border-[#313131] px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-400 transition hover:bg-gray-50 dark:hover:bg-[#313131]">
              Voir tout
            </Link>
          </div>
        </div>

        <div ref={scrollRef} className="mt-4 flex gap-3 overflow-x-auto scrollbar-hide pb-2 -mx-1 px-1">
          <div className="flex shrink-0 flex-col items-center justify-center rounded-2xl border border-gray-200 dark:border-[#313131] bg-white dark:bg-[#2A2A2A] px-6 py-5 min-w-[140px] gap-1.5">
            <p className="text-3xl font-extrabold text-gray-900 dark:text-white leading-none">{averageRating.toFixed(1)}</p>
            <div className="flex items-center gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} fill="currentColor" className={`h-4 w-4 ${i < Math.round(averageRating) ? "text-yellow-400" : "text-gray-200 dark:text-gray-600"}`} />
              ))}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {totalReviews >= 1000 ? `${(totalReviews / 1000).toFixed(0)}k+` : totalReviews} avis
            </p>
          </div>
          {reviews.map((review) => (
            <ReviewCard key={review.ulid} review={review} onClick={() => setSelectedReview(review)} />
          ))}
        </div>
      </div>

      {selectedReview && (
        <ReviewDetailModal review={selectedReview} onClose={() => setSelectedReview(null)} />
      )}
    </>
  );
}

function ReviewCard({ review, onClick }: { review: StoreReview; onClick: () => void }) {
  const dateStr = formatReviewDate(review.created_at);
  const items = review.order_items ?? [];
  const firstItem = items[0];
  const extra = items.length - 1;

  return (
    <button
      onClick={onClick}
      className="flex shrink-0 w-[260px] flex-col rounded-2xl border border-gray-200 dark:border-[#313131] bg-white dark:bg-[#2A2A2A] p-4 shadow-sm text-left transition hover:border-gray-300 dark:hover:border-[#444] hover:shadow-md"
    >
      {/* Name + date */}
      <div className="flex items-start justify-between gap-2">
        <p className="truncate text-sm font-bold text-gray-900 dark:text-white">
          {review.customer.firstname}{review.customer.lastname ? ` ${review.customer.lastname.slice(0, 1)}.` : ""}
        </p>
        <p className="shrink-0 text-[11px] text-gray-400 dark:text-gray-500">{dateStr}</p>
      </div>

      {/* Stars */}
      <div className="mt-1.5 flex items-center gap-0.5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star key={i} fill="currentColor" className={`h-3.5 w-3.5 ${i < Math.round(review.rating) ? "text-yellow-400" : "text-gray-200 dark:text-gray-600"}`} />
        ))}
      </div>

      {/* Comment */}
      {review.comment && (
        <p className="mt-2 line-clamp-3 text-[13px] leading-snug text-gray-700 dark:text-gray-300">{review.comment}</p>
      )}

      {/* Product chips */}
      {items.length > 0 && (
        <div className="mt-auto flex items-center gap-1.5 overflow-hidden pt-3">
          {firstItem && (
            <span className="max-w-[150px] shrink-0 truncate rounded-full bg-gray-100 dark:bg-[#313131] px-2.5 py-1 text-[11px] font-medium text-gray-600 dark:text-gray-300">
              {firstItem.name}
            </span>
          )}
          {extra > 0 && (
            <span className="shrink-0 rounded-full bg-gray-100 dark:bg-[#313131] px-2.5 py-1 text-[11px] font-medium text-gray-500 dark:text-gray-400">
              +{extra} produit{extra > 1 ? "s" : ""}
            </span>
          )}
        </div>
      )}
    </button>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  CATEGORY ICON BAR (grocery)                                  */
/* ══════════════════════════════════════════════════════════════ */

const CAT_GRADIENTS = [
  "from-green-400 to-emerald-500",
  "from-orange-400 to-amber-500",
  "from-blue-400 to-sky-500",
  "from-purple-400 to-violet-500",
  "from-pink-400 to-rose-500",
  "from-red-400 to-rose-600",
  "from-teal-400 to-cyan-500",
  "from-yellow-300 to-amber-400",
  "from-indigo-400 to-blue-500",
  "from-lime-400 to-green-500",
];

function catGradient(name: string): string {
  let h = 0;
  for (const c of name) h = c.charCodeAt(0) + ((h << 5) - h);
  return CAT_GRADIENTS[Math.abs(h) % CAT_GRADIENTS.length];
}

function CategoryIconBar({
  categories,
  activeCategory,
  onSelect,
  onViewAll,
}: {
  categories: MenuCategory[];
  activeCategory: string | null;
  onSelect: (id: string) => void;
  onViewAll: () => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const scroll = useCallback((dir: "left" | "right") => {
    if (!scrollRef.current) return;
    const first = scrollRef.current.querySelector<HTMLElement>(":scope > *");
    const w = first ? first.offsetWidth + 20 : 100;
    scrollRef.current.scrollBy({ left: dir === "right" ? w * 3 : -(w * 3), behavior: "smooth" });
  }, []);

  return (
    <div className="-mx-4 md:-mx-6 lg:-mx-8 border-b border-gray-100 dark:border-[#313131] pt-4 pb-2">
      <div className="flex items-center justify-between px-4 md:px-6 lg:px-8 mb-3">
        <h3 className="text-sm font-bold text-gray-700 dark:text-gray-400">Catégories</h3>
        <div className="flex items-center gap-1.5">
          <button onClick={() => scroll("left")} className="flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 dark:border-[#313131] bg-white dark:bg-[#2A2A2A] hover:bg-gray-50 dark:hover:bg-[#313131] transition shadow-sm" aria-label="Précédent">
            <ChevronLeft className="h-3.5 w-3.5" strokeWidth={2} />
          </button>
          <button onClick={() => scroll("right")} className="flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 dark:border-[#313131] bg-white dark:bg-[#2A2A2A] hover:bg-gray-50 dark:hover:bg-[#313131] transition shadow-sm" aria-label="Suivant">
            <ChevronRight className="h-3.5 w-3.5" strokeWidth={2} />
          </button>
          <button onClick={onViewAll} className="rounded-full border border-gray-200 dark:border-[#313131] bg-white dark:bg-[#2A2A2A] px-3 py-1 text-xs font-semibold text-gray-700 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#313131] transition shadow-sm">
            Voir tout
          </button>
        </div>
      </div>
      <div ref={scrollRef} className="flex gap-5 overflow-x-auto scrollbar-hide px-4 md:px-6 lg:px-8 py-2">
        {categories.map((cat) => {
          const isActive = activeCategory === cat.ulid;
          return (
            <button key={cat.ulid} onClick={() => onSelect(cat.ulid)} className="flex shrink-0 flex-col items-center gap-2 group">
              <div className={`h-16 w-16 rounded-2xl overflow-hidden bg-gradient-to-br ${catGradient(cat.name)} flex items-center justify-center shadow-sm transition group-hover:scale-105 ${isActive ? "ring-2 ring-gray-900 ring-offset-2" : ""}`}>
                {cat.image ? (
                  <Image src={cat.image} alt={cat.name} width={64} height={64} className="h-full w-full object-cover" />
                ) : (
                  <span className="text-2xl font-black text-white select-none leading-none">{cat.name.slice(0, 1).toUpperCase()}</span>
                )}
              </div>
              <span className={`text-[11px] font-semibold whitespace-nowrap transition ${isActive ? "text-gray-900 dark:text-white border-b border-gray-900 pb-0.5" : "text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200"}`}>
                {cat.name}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  GROCERY BROWSE ALL VIEW                                      */
/* ══════════════════════════════════════════════════════════════ */

function GroceryBrowseAllView({
  categories,
  storeName,
  onSelectCategory,
  onBack,
}: {
  categories: MenuCategory[];
  storeName: string;
  onSelectCategory: (cat: MenuCategory) => void;
  onBack: () => void;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-4">
        <button onClick={onBack} className="font-semibold text-gray-700 dark:text-white hover:underline underline-offset-2 transition">{storeName}</button>
        <ChevronRight className="h-4 w-4 text-gray-300 dark:text-gray-400" strokeWidth={2} />
        <span className="font-semibold text-gray-900 dark:text-white">Tout parcourir</span>
      </div>
      <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-6">Tout parcourir</h1>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {categories.map((cat) => (
          <button key={cat.ulid} onClick={() => onSelectCategory(cat)} className="flex flex-col items-center gap-2.5 group rounded-2xl p-2 hover:bg-gray-50 dark:hover:bg-[#313131] transition">
            <div className={`w-full aspect-square rounded-2xl overflow-hidden bg-gradient-to-br ${catGradient(cat.name)} flex items-center justify-center shadow-sm transition group-hover:scale-[1.04]`}>
              {cat.image ? (
                <Image src={cat.image} alt={cat.name} width={160} height={160} className="h-full w-full object-cover" />
              ) : (
                <span className="text-3xl font-black text-white select-none">{cat.name.slice(0, 1).toUpperCase()}</span>
              )}
            </div>
            <span className="text-[13px] font-semibold text-gray-700 dark:text-gray-400 text-center leading-tight line-clamp-2 group-hover:text-gray-900 dark:group-hover:text-white transition">
              {cat.name}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  GROCERY CATEGORY VIEW                                        */
/* ══════════════════════════════════════════════════════════════ */

type GrocerySortKey = "priority" | "price_asc" | "price_desc" | "name";
type PriceRange = "u1000" | "1k-3k" | "3k-7k" | "o7k" | null;

interface GroceryFilters {
  priceRange: PriceRange;
  discountOnly: boolean;
  inStockOnly: boolean;
  sortKey: GrocerySortKey;
}

const PRICE_RANGES: { key: PriceRange; label: string; min: number; max: number }[] = [
  { key: "u1000", label: "Moins de 1 000 DA", min: 0, max: 999 },
  { key: "1k-3k", label: "1 000 – 3 000 DA", min: 1000, max: 3000 },
  { key: "3k-7k", label: "3 000 – 7 000 DA", min: 3000, max: 7000 },
  { key: "o7k", label: "Plus de 7 000 DA", min: 7001, max: Infinity },
];

const SORT_LABELS: Record<GrocerySortKey, string> = {
  priority: "Pertinence",
  price_asc: "Prix croissant",
  price_desc: "Prix décroissant",
  name: "Nom A–Z",
};

function applyFilters(products: MenuProduct[], f: GroceryFilters): MenuProduct[] {
  let arr = [...products];
  if (f.priceRange) {
    const range = PRICE_RANGES.find((r) => r.key === f.priceRange)!;
    arr = arr.filter((p) => {
      const price = p.discount_applied ? p.discounted_price : Number(p.price);
      return price >= range.min && price <= range.max;
    });
  }
  if (f.discountOnly) arr = arr.filter((p) => p.discount_applied && p.savings > 0);
  if (f.inStockOnly) arr = arr.filter((p) => !isProductOutOfStock(p));
  if (f.sortKey === "price_asc") arr.sort((a, b) => Number(a.price) - Number(b.price));
  if (f.sortKey === "price_desc") arr.sort((a, b) => Number(b.price) - Number(a.price));
  if (f.sortKey === "name") arr.sort((a, b) => a.name.localeCompare(b.name));
  return arr;
}

const DEFAULT_FILTERS: GroceryFilters = { priceRange: null, discountOnly: false, inStockOnly: false, sortKey: "priority" };

function activeFilterCount(f: GroceryFilters) {
  return (f.priceRange ? 1 : 0) + (f.discountOnly ? 1 : 0) + (f.inStockOnly ? 1 : 0) + (f.sortKey !== "priority" ? 1 : 0);
}

function GroceryCategoryView({
  category,
  storeUlid,
  storeName,
  onProductClick,
  onBack,
  onBrowseAll,
  onSelectCategory,
}: {
  category: MenuCategory;
  storeUlid: string;
  storeName: string;
  onProductClick: (p: MenuProduct) => void;
  onBack: () => void;
  onBrowseAll: () => void;
  onSelectCategory: (cat: MenuCategory) => void;
}) {
  const { data: rawData, isLoading } = useStoreMenuProducts(storeUlid, category.ulid);
  const [filters, setFilters] = useState<GroceryFilters>(DEFAULT_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [pendingFilters, setPendingFilters] = useState<GroceryFilters>(DEFAULT_FILTERS);
  const [showSort, setShowSort] = useState(false);

  // The multi-level endpoint returns sub-categories or products for this category.
  // Detect by shape: products carry a `price`; sub-categories don't.
  const rawItems: unknown[] = Array.isArray(rawData) ? rawData : [];
  const firstItem = rawItems[0] as Record<string, unknown> | undefined;
  const isProductLevel = rawItems.length === 0 || typeof firstItem?.price !== "undefined";
  const productList = isProductLevel ? (rawItems as MenuProduct[]) : [];
  const subCatList = !isProductLevel ? (rawItems as MenuCategory[]) : [];

  const filtered = applyFilters(productList, filters);
  const filterCount = activeFilterCount(filters);

  function openFilters() { setPendingFilters(filters); setShowFilters(true); }
  function applyPending() { setFilters(pendingFilters); setShowFilters(false); }

  const activeChips: { label: string; onRemove: () => void }[] = [];
  if (filters.priceRange) {
    const r = PRICE_RANGES.find((p) => p.key === filters.priceRange)!;
    activeChips.push({ label: r.label, onRemove: () => setFilters((f) => ({ ...f, priceRange: null })) });
  }
  if (filters.discountOnly) activeChips.push({ label: "Promotions uniquement", onRemove: () => setFilters((f) => ({ ...f, discountOnly: false })) });
  if (filters.inStockOnly) activeChips.push({ label: "En stock uniquement", onRemove: () => setFilters((f) => ({ ...f, inStockOnly: false })) });
  if (filters.sortKey !== "priority") activeChips.push({ label: SORT_LABELS[filters.sortKey], onRemove: () => setFilters((f) => ({ ...f, sortKey: "priority" })) });

  return (
    <div className="relative">
      {showFilters && <div className="fixed inset-0 z-40 bg-black/30" onClick={() => setShowFilters(false)} />}

      {/* Filter drawer */}
      <div className={`fixed inset-y-0 left-0 z-50 w-[300px] bg-white dark:bg-[#2A2A2A] shadow-2xl flex flex-col transition-transform duration-300 ${showFilters ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 dark:border-[#313131] shrink-0">
          <button onClick={() => setShowFilters(false)} className="text-gray-500 hover:text-gray-900 dark:hover:text-white transition">
            <X className="h-5 w-5" strokeWidth={2} />
          </button>
          <span className="text-base font-bold text-gray-900 dark:text-white">Filtres</span>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-3">Trier par</h3>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(SORT_LABELS) as GrocerySortKey[]).map((k) => (
                <button key={k} onClick={() => setPendingFilters((f) => ({ ...f, sortKey: k }))}
                  className={`rounded-full px-3 py-1.5 text-sm font-semibold border transition ${pendingFilters.sortKey === k ? "bg-gray-900 text-white border-gray-900" : "border-gray-200 dark:border-[#313131] text-gray-700 dark:text-gray-400 hover:border-gray-400"}`}>
                  {SORT_LABELS[k]}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">Prix</h3>
              {pendingFilters.priceRange && (
                <button onClick={() => setPendingFilters((f) => ({ ...f, priceRange: null }))} className="text-xs font-semibold text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition">Effacer</button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {PRICE_RANGES.map((r) => (
                <button key={r.key!} onClick={() => setPendingFilters((f) => ({ ...f, priceRange: f.priceRange === r.key ? null : r.key }))}
                  className={`rounded-full px-3 py-1.5 text-sm font-semibold border transition ${pendingFilters.priceRange === r.key ? "bg-gray-900 text-white border-gray-900" : "border-gray-200 dark:border-[#313131] text-gray-700 dark:text-gray-400 hover:border-gray-400"}`}>
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-3">Options</h3>
            <div className="space-y-2">
              {(["discountOnly", "inStockOnly"] as const).map((key) => (
                <button key={key} onClick={() => setPendingFilters((f) => ({ ...f, [key]: !f[key] }))}
                  className={`w-full flex items-center justify-between rounded-xl border px-4 py-3 text-sm font-semibold transition ${pendingFilters[key] ? "bg-gray-900 text-white border-gray-900" : "border-gray-200 dark:border-[#313131] text-gray-700 dark:text-gray-400 hover:border-gray-400"}`}>
                  {key === "discountOnly" ? "Promotions uniquement" : "En stock uniquement"}
                  {pendingFilters[key] && <Check className="h-4 w-4" strokeWidth={2.5} />}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="border-t border-gray-100 dark:border-[#313131] p-4 flex gap-3 shrink-0">
          <button onClick={() => setPendingFilters(DEFAULT_FILTERS)} className="flex-1 rounded-full border border-gray-200 dark:border-[#313131] py-2.5 text-sm font-bold text-gray-700 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#313131] transition">
            Effacer tout
          </button>
          <button onClick={applyPending} className="flex-1 rounded-full bg-gray-900 py-2.5 text-sm font-bold text-white hover:bg-gray-800 transition">
            Voir les résultats
          </button>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-4">
        <button onClick={onBack} className="font-semibold text-gray-700 dark:text-white hover:underline underline-offset-2 transition">{storeName}</button>
        <ChevronRight className="h-4 w-4 text-gray-300 dark:text-gray-400" strokeWidth={2} />
        <button onClick={onBrowseAll} className="font-semibold text-gray-700 dark:text-white hover:underline underline-offset-2 transition">Tout parcourir</button>
        <ChevronRight className="h-4 w-4 text-gray-300 dark:text-gray-400" strokeWidth={2} />
        <span className="font-semibold text-gray-900 dark:text-white">{category.name}</span>
      </div>

      <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-5">{category.name}</h1>

      {/* Toolbar — products only (hidden when browsing sub-categories) */}
      {isProductLevel && (
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <button onClick={openFilters}
          className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition ${filterCount > 0 ? "bg-gray-900 text-white border-gray-900" : "border-gray-200 dark:border-[#313131] text-gray-700 dark:text-gray-400 hover:border-gray-400 bg-white dark:bg-[#2A2A2A]"}`}>
          <SlidersHorizontal className="h-4 w-4" strokeWidth={2} />
          Filtrer{filterCount > 0 ? ` (${filterCount})` : ""}
        </button>

        {activeChips.map((chip) => (
          <span key={chip.label} className="flex items-center gap-1.5 rounded-full bg-gray-900 pl-3 pr-2 py-1.5 text-xs font-bold text-white">
            {chip.label}
            <button onClick={chip.onRemove} className="flex h-4 w-4 items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition">
              <X className="h-2.5 w-2.5" strokeWidth={2} />
            </button>
          </span>
        ))}

        <div className="relative ml-auto">
          <button onClick={() => setShowSort((v) => !v)}
            className="flex items-center gap-2 rounded-full border border-gray-200 dark:border-[#313131] bg-white dark:bg-[#2A2A2A] px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#313131] transition shadow-sm">
            Trier : {SORT_LABELS[filters.sortKey]}
            <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showSort ? "rotate-180" : ""}`} strokeWidth={2} />
          </button>
          {showSort && (
            <div className="absolute right-0 top-full mt-1 z-20 w-48 rounded-xl border border-gray-100 dark:border-[#313131] bg-white dark:bg-[#2A2A2A] py-1 shadow-lg">
              {(Object.keys(SORT_LABELS) as GrocerySortKey[]).map((k) => (
                <button key={k} onClick={() => { setFilters((f) => ({ ...f, sortKey: k })); setShowSort(false); }}
                  className={`flex w-full items-center justify-between px-4 py-2.5 text-sm transition hover:bg-gray-50 dark:hover:bg-[#313131] ${filters.sortKey === k ? "font-bold text-gray-900 dark:text-white" : "text-gray-700 dark:text-gray-400"}`}>
                  {SORT_LABELS[k]}
                  {filters.sortKey === k && <Check className="h-4 w-4" strokeWidth={2.5} />}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      )}

      {isProductLevel && (
        <p className="text-sm text-gray-500 dark:text-gray-400 font-medium mb-5">
          {isLoading ? "Chargement…" : `${filtered.length} résultat${filtered.length !== 1 ? "s" : ""}`}
        </p>
      )}

      {isLoading ? (
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 animate-pulse">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i}>
              <div className="aspect-square rounded-xl bg-gray-200 dark:bg-[#313131]" />
              <div className="mt-2 h-3 w-3/4 rounded bg-gray-200 dark:bg-[#313131]" />
              <div className="mt-1 h-3 w-1/2 rounded bg-gray-100 dark:bg-[#313131]" />
            </div>
          ))}
        </div>
      ) : !isProductLevel ? (
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {subCatList.map((subCat) => (
            <button key={subCat.ulid} onClick={() => onSelectCategory(subCat)} className="group text-left">
              <div className="relative aspect-square overflow-hidden rounded-xl bg-gray-100 dark:bg-[#313131]">
                {subCat.image ? (
                  <Image src={subCat.image} alt={subCat.name} fill className="object-cover transition group-hover:scale-105" sizes="(max-width: 640px) 50vw, 25vw" />
                ) : (
                  <div className="flex h-full items-center justify-center">
                    <span className="text-3xl font-black text-gray-300 dark:text-gray-600 select-none">{subCat.name.slice(0, 1).toUpperCase()}</span>
                  </div>
                )}
              </div>
              <p className="mt-2 text-[13px] font-semibold text-gray-900 dark:text-white line-clamp-2 leading-tight">{subCat.name}</p>
            </button>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
          <Search className="h-10 w-10 text-gray-200" strokeWidth={1} />
          <p className="text-gray-500 dark:text-gray-400 text-sm">Aucun produit ne correspond à ces filtres.</p>
          {filterCount > 0 && (
            <button onClick={() => setFilters(DEFAULT_FILTERS)} className="rounded-full bg-gray-900 px-4 py-2 text-sm font-bold text-white hover:bg-gray-800 transition">Effacer les filtres</button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {filtered.map((product) => (
            <GroceryProductCard key={product.ulid} product={product} storeUlid={storeUlid} onClick={() => onProductClick(product)} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  GROCERY CAROUSEL SECTION                                     */
/* ══════════════════════════════════════════════════════════════ */

const GroceryCarouselSection = forwardRef<
  HTMLDivElement,
  {
    category: MenuCategory;
    storeUlid: string;
    onProductClick: (p: MenuProduct) => void;
    onSelectCategory: (cat: MenuCategory) => void;
  }
>(function GroceryCarouselSection({ category, storeUlid, onProductClick, onSelectCategory }, ref) {
  // Grocery categories nest (Chat → Croquettes → products); walk every
  // descendant so the carousel shows real products like the mobile app.
  const { data: products, isLoading } = useStoreCategoryProductsDeep(storeUlid, category.ulid);
  const productList = products ?? [];
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = useCallback((dir: "left" | "right") => {
    if (!scrollRef.current) return;
    const first = scrollRef.current.querySelector<HTMLElement>(":scope > *");
    const w = first ? first.offsetWidth + 12 : 172;
    scrollRef.current.scrollBy({ left: dir === "right" ? w * 3 : -w * 3, behavior: "smooth" });
  }, []);

  // Hide a category that resolved to zero products (e.g. empty sub-tree).
  if (!isLoading && productList.length === 0) return null;

  return (
    <section ref={ref} data-cat-id={category.ulid} className="mb-10 scroll-mt-24">
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => onSelectCategory(category)} className="group flex items-center gap-1.5">
          <h2 className="text-[19px] font-bold text-gray-900 dark:text-white group-hover:underline underline-offset-4">{category.name}</h2>
          <ChevronRight className="h-4 w-4 text-gray-400 transition group-hover:translate-x-0.5" strokeWidth={2.5} />
        </button>
        <div className="flex items-center gap-2">
          <button onClick={() => scroll("left")} className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 dark:border-[#313131] bg-white dark:bg-[#2A2A2A] hover:bg-gray-50 dark:hover:bg-[#313131] transition shadow-sm" aria-label="Précédent">
            <ChevronLeft className="h-4 w-4" strokeWidth={2} />
          </button>
          <button onClick={() => scroll("right")} className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 dark:border-[#313131] bg-white dark:bg-[#2A2A2A] hover:bg-gray-50 dark:hover:bg-[#313131] transition shadow-sm" aria-label="Suivant">
            <ChevronRight className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>
      </div>
      <div className="relative">
        <div ref={scrollRef} className="flex gap-3 overflow-x-auto scrollbar-hide scroll-smooth">
          {isLoading
            ? Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="shrink-0 w-[160px] animate-pulse">
                <div className="aspect-square rounded-xl bg-gray-200 dark:bg-[#313131]" />
                <div className="mt-2 px-0.5 space-y-1.5">
                  <div className="h-3 w-full rounded bg-gray-200 dark:bg-[#313131]" />
                  <div className="h-3 w-2/3 rounded bg-gray-200 dark:bg-[#313131]" />
                  <div className="h-3 w-1/3 rounded bg-gray-100 dark:bg-[#313131]" />
                </div>
              </div>
            ))
            : productList.map((product) => (
              <GroceryProductCard key={product.ulid} product={product} storeUlid={storeUlid} onClick={() => onProductClick(product)} />
            ))}
        </div>
        <div className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-white dark:from-[#1E1E1E] to-transparent" />
      </div>
    </section>
  );
});

/* ══════════════════════════════════════════════════════════════ */
/*  GROCERY PRODUCT CARD                                         */
/* ══════════════════════════════════════════════════════════════ */

function GroceryProductCard({
  product,
  storeUlid,
  onClick,
}: {
  product: MenuProduct;
  storeUlid: string;
  onClick: () => void;
}) {
  const price = Number(product.price);
  const hasDiscount = product.discount_applied && product.savings > 0;
  const finalPrice = hasDiscount ? product.discounted_price : price;
  const isOutOfStock = isProductOutOfStock(product);
  const ctrl = useProductCartControl(storeUlid, product, onClick);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onClick(); }}
      className="shrink-0 w-[160px] cursor-pointer select-none group"
    >
      <div className="relative aspect-square overflow-hidden rounded-xl bg-gray-100 dark:bg-[#313131]">
        <Image
          src={product.image ?? "/fast-logo.svg"}
          alt={product.name}
          fill
          className={product.image ? "object-cover transition group-hover:scale-105" : "object-contain p-6 opacity-20 dark:opacity-10"}
          sizes="160px"
        />
        {hasDiscount && (
          <span className="absolute top-2 left-2 rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white leading-none">
            -{formatAmount(product.savings)} DA
          </span>
        )}
        {isOutOfStock ? (
          <span className="absolute bottom-2 right-2 rounded-full bg-gray-200/90 dark:bg-gray-700/90 px-2 py-0.5 text-[10px] font-semibold text-gray-600 dark:text-gray-400 leading-none">
            Épuisé
          </span>
        ) : (
          <div className="absolute bottom-2 right-2">
            <AddControl cartQty={ctrl.cartQty} showStepper={ctrl.showStepper} busy={ctrl.busy} onPlus={ctrl.onPlus} onMinus={ctrl.onMinus} size="md" />
          </div>
        )}
      </div>
      <div className="mt-2 px-0.5">
        <p className="text-[13px] font-semibold text-gray-900 dark:text-white line-clamp-2 leading-tight">{product.name}</p>
        <div className="mt-1 flex items-center gap-1.5 flex-wrap">
          <span className="text-[13px] font-bold text-gray-900 dark:text-white">{formatAmount(finalPrice)} DA</span>
          {hasDiscount && (
            <span className="text-[11px] text-gray-400 dark:text-gray-500 line-through">{formatAmount(price)} DA</span>
          )}
        </div>
        {(trackedStock(product) ?? Infinity) <= 5 && (
          <p className="mt-0.5 text-[11px] text-orange-500 font-medium">{product.stock} en stock</p>
        )}
      </div>
      {ctrl.cartAuthModal}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  NON-GROCERY MENU BLOCK                                       */
/* ══════════════════════════════════════════════════════════════ */

function AllProductsView({
  categories,
  storeUlid,
  filters,
  onProductClick,
}: {
  categories: MenuCategory[];
  storeUlid: string;
  filters: GroceryFilters;
  onProductClick: (p: MenuProduct) => void;
}) {
  const { products, isLoading } = useAllStoreProducts(storeUlid, categories);
  const filtered = applyFilters(products, filters);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 animate-pulse">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex gap-4 rounded-xl border border-gray-100 dark:border-[#313131] p-4">
            <div className="flex-1 space-y-2 py-0.5">
              <div className="h-4 w-3/4 rounded bg-gray-200 dark:bg-[#313131]" />
              <div className="h-3 w-full rounded bg-gray-100 dark:bg-[#313131]" />
              <div className="h-3 w-1/3 rounded bg-gray-100 dark:bg-[#313131]" />
            </div>
            <div className="h-[110px] w-[128px] shrink-0 rounded-xl bg-gray-200 dark:bg-[#313131]" />
          </div>
        ))}
      </div>
    );
  }

  if (filtered.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
        <Search className="h-10 w-10 text-gray-200" strokeWidth={1} />
        <p className="text-gray-500 dark:text-gray-400 text-sm">Aucun produit ne correspond à ces filtres.</p>
      </div>
    );
  }

  return (
    <div>
      <p className="text-sm text-gray-500 dark:text-gray-400 font-medium mb-4">
        {filtered.length} résultat{filtered.length !== 1 ? "s" : ""}
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {filtered.map((product) => (
          <ProductCard key={product.ulid} product={product} storeUlid={storeUlid} onProductClick={onProductClick} />
        ))}
      </div>
    </div>
  );
}

function NonGroceryMenuBlock({
  categories,
  storeUlid,
  storeName,
  onProductClick,
  sectionRefs,
}: {
  categories: MenuCategory[];
  storeUlid: string;
  storeName: string;
  onProductClick: (p: MenuProduct) => void;
  sectionRefs: React.RefObject<Map<string, HTMLDivElement>>;
}) {
  const [filters, setFilters] = useState<GroceryFilters>(DEFAULT_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [pendingFilters, setPendingFilters] = useState<GroceryFilters>(DEFAULT_FILTERS);
  const [showSort, setShowSort] = useState(false);

  const filterCount = activeFilterCount(filters);
  const isFiltered = filterCount > 0;

  void storeName;

  function openFilters() { setPendingFilters(filters); setShowFilters(true); }
  function applyPending() { setFilters(pendingFilters); setShowFilters(false); }

  const activeChips: { label: string; onRemove: () => void }[] = [];
  if (filters.priceRange) {
    const r = PRICE_RANGES.find((p) => p.key === filters.priceRange)!;
    activeChips.push({ label: r.label, onRemove: () => setFilters((f) => ({ ...f, priceRange: null })) });
  }
  if (filters.discountOnly) activeChips.push({ label: "Promotions uniquement", onRemove: () => setFilters((f) => ({ ...f, discountOnly: false })) });
  if (filters.inStockOnly) activeChips.push({ label: "En stock uniquement", onRemove: () => setFilters((f) => ({ ...f, inStockOnly: false })) });
  if (filters.sortKey !== "priority") activeChips.push({ label: SORT_LABELS[filters.sortKey], onRemove: () => setFilters((f) => ({ ...f, sortKey: "priority" })) });

  return (
    <div className="relative">
      {showFilters && <div className="fixed inset-0 z-40 bg-black/30" onClick={() => setShowFilters(false)} />}

      {/* Filter drawer */}
      <div className={`fixed inset-y-0 left-0 z-50 w-[300px] bg-white dark:bg-[#2A2A2A] shadow-2xl flex flex-col transition-transform duration-300 ${showFilters ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 dark:border-[#313131] shrink-0">
          <button onClick={() => setShowFilters(false)} className="text-gray-500 hover:text-gray-900 dark:hover:text-white transition">
            <X className="h-5 w-5" strokeWidth={2} />
          </button>
          <span className="text-base font-bold text-gray-900 dark:text-white">Filtres</span>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-3">Trier par</h3>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(SORT_LABELS) as GrocerySortKey[]).map((k) => (
                <button key={k} onClick={() => setPendingFilters((f) => ({ ...f, sortKey: k }))}
                  className={`rounded-full px-3 py-1.5 text-sm font-semibold border transition ${pendingFilters.sortKey === k ? "bg-gray-900 text-white border-gray-900" : "border-gray-200 dark:border-[#313131] text-gray-700 dark:text-gray-400 hover:border-gray-400"}`}>
                  {SORT_LABELS[k]}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">Prix</h3>
              {pendingFilters.priceRange && (
                <button onClick={() => setPendingFilters((f) => ({ ...f, priceRange: null }))} className="text-xs font-semibold text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition">Effacer</button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {PRICE_RANGES.map((r) => (
                <button key={r.key!} onClick={() => setPendingFilters((f) => ({ ...f, priceRange: f.priceRange === r.key ? null : r.key }))}
                  className={`rounded-full px-3 py-1.5 text-sm font-semibold border transition ${pendingFilters.priceRange === r.key ? "bg-gray-900 text-white border-gray-900" : "border-gray-200 dark:border-[#313131] text-gray-700 dark:text-gray-400 hover:border-gray-400"}`}>
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-3">Options</h3>
            <div className="space-y-2">
              {(["discountOnly", "inStockOnly"] as const).map((key) => (
                <button key={key} onClick={() => setPendingFilters((f) => ({ ...f, [key]: !f[key] }))}
                  className={`w-full flex items-center justify-between rounded-xl border px-4 py-3 text-sm font-semibold transition ${pendingFilters[key] ? "bg-gray-900 text-white border-gray-900" : "border-gray-200 dark:border-[#313131] text-gray-700 dark:text-gray-400 hover:border-gray-400"}`}>
                  {key === "discountOnly" ? "Promotions uniquement" : "En stock uniquement"}
                  {pendingFilters[key] && <Check className="h-4 w-4" strokeWidth={2.5} />}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="border-t border-gray-100 dark:border-[#313131] p-4 flex gap-3 shrink-0">
          <button onClick={() => setPendingFilters(DEFAULT_FILTERS)} className="flex-1 rounded-full border border-gray-200 dark:border-[#313131] py-2.5 text-sm font-bold text-gray-700 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#313131] transition">
            Effacer tout
          </button>
          <button onClick={applyPending} className="flex-1 rounded-full bg-gray-900 py-2.5 text-sm font-bold text-white hover:bg-gray-800 transition">
            Voir les résultats
          </button>
        </div>
      </div>

      {/* Mobile category navigation is handled by the sticky tab bar in StoreView. */}

      {/* Filter / Sort toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {/* Active chips stay on the left */}
        {activeChips.map((chip) => (
          <span
            key={chip.label}
            className="flex items-center gap-1.5 rounded-full bg-gray-900 pl-3 pr-2 py-1.5 text-xs font-bold text-white"
          >
            {chip.label}

            <button
              onClick={chip.onRemove}
              className="flex h-4 w-4 items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition"
            >
              <X className="h-2.5 w-2.5" strokeWidth={2} />
            </button>
          </span>
        ))}

        {/* Right side actions */}
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={openFilters}
            className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition ${filterCount > 0
             ? "border-gray-200 dark:border-[#313131] bg-white dark:bg-[#2A2A2A] text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-[#313131]"
        : "border-gray-200 dark:border-[#313131] bg-white dark:bg-[#2A2A2A] text-gray-700 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#313131]"
              }`}
          >
            <SlidersHorizontal className="h-4 w-4" strokeWidth={2} />

            Filtrer{filterCount > 0 ? ` (${filterCount})` : ""}
          </button>

          <div className="relative">
            <button
              onClick={() => setShowSort((v) => !v)}
              className="flex items-center gap-2 rounded-full border border-gray-200 dark:border-[#313131] bg-white dark:bg-[#2A2A2A] px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#313131] transition shadow-sm"
            >
              Trier : {SORT_LABELS[filters.sortKey]}

              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform ${showSort ? "rotate-180" : ""}`}
                strokeWidth={2}
              />
            </button>

            {showSort && (
              <div className="absolute right-0 top-full mt-1 z-20 w-48 rounded-xl border border-gray-100 dark:border-[#313131] bg-white dark:bg-[#2A2A2A] py-1 shadow-lg">
                {(Object.keys(SORT_LABELS) as GrocerySortKey[]).map((k) => (
                  <button
                    key={k}
                    onClick={() => {
                      setFilters((f) => ({ ...f, sortKey: k }));
                      setShowSort(false);
                    }}
                    className={`flex w-full items-center justify-between px-4 py-2.5 text-sm transition hover:bg-gray-50 dark:hover:bg-[#313131] ${filters.sortKey === k
                        ? "font-bold text-gray-900 dark:text-white"
                        : "text-gray-700 dark:text-gray-400"
                      }`}
                  >
                    {SORT_LABELS[k]}

                    {filters.sortKey === k && (
                      <Check className="h-4 w-4" strokeWidth={2.5} />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {isFiltered ? (
        <AllProductsView categories={categories} storeUlid={storeUlid} filters={filters} onProductClick={onProductClick} />
      ) : (
        <div>
          {categories.map((cat) => (
            <MenuCategorySection
              key={cat.ulid}
              category={cat}
              storeUlid={storeUlid}
              onProductClick={onProductClick}
              ref={(el) => { if (el) sectionRefs.current.set(cat.ulid, el); }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  MENU CATEGORY SECTION                                        */
/* ══════════════════════════════════════════════════════════════ */

const MenuCategorySection = forwardRef<
  HTMLDivElement,
  { category: MenuCategory; storeUlid: string; onProductClick: (p: MenuProduct) => void }
>(function MenuCategorySection({ category, storeUlid, onProductClick }, ref) {
  // Flat menus nest their products in the multi-level categories response — use
  // those directly and only fetch per-category when they're missing.
  const hasNested = Array.isArray(category.products);
  const { data: products, isLoading: isFetching } = useStoreMenuProducts(
    storeUlid,
    category.ulid,
    { enabled: !hasNested }
  );
  const isLoading = !hasNested && isFetching;

  // The multi-level endpoint may return MenuProduct[] or [{name, products:[]}]
  const productList: MenuProduct[] = (() => {
    if (hasNested) return category.products!;
    if (!products) return [];
    if (!Array.isArray(products)) {
      const obj = products as { products?: MenuProduct[]; data?: MenuProduct[] };
      return obj?.products ?? obj?.data ?? [];
    }
    const first = products[0] as { products?: MenuProduct[] };
    if (first && Array.isArray(first.products)) {
      return (products as Array<{ products?: MenuProduct[] }>).flatMap((cat) => cat.products ?? []);
    }
    return products as MenuProduct[];
  })();

  // Don't render a category that has no products.
  if (!isLoading && productList.length === 0) return null;

  return (
    <section ref={ref} data-cat-id={category.ulid} className="mb-10 scroll-mt-[104px] md:scroll-mt-24">
      <h2 className="text-[19px] font-bold text-gray-900 dark:text-white">{category.name}</h2>

      {isLoading && (
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 animate-pulse rounded-2xl border border-gray-200 dark:border-[#313131] p-4">
              <div className="flex-1 space-y-2.5">
                <div className="h-4 w-3/4 rounded bg-gray-200 dark:bg-[#313131]" />
                <div className="h-3 w-full rounded bg-gray-100 dark:bg-[#313131]" />
                <div className="h-3 w-1/3 rounded bg-gray-100 dark:bg-[#313131]" />
              </div>
              <div className="h-[130px] w-[150px] shrink-0 rounded-2xl bg-gray-200 dark:bg-[#313131]" />
            </div>
          ))}
        </div>
      )}

      {productList.length > 0 && (
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
          {productList.map((product) => (
            <ProductCard key={product.ulid} product={product} storeUlid={storeUlid} onProductClick={onProductClick} />
          ))}
        </div>
      )}
    </section>
  );
});

/* ══════════════════════════════════════════════════════════════ */
/*  PRODUCT CARD                                                 */
/* ══════════════════════════════════════════════════════════════ */

function ProductCard({
  product,
  storeUlid,
  onProductClick,
}: {
  product: MenuProduct;
  storeUlid: string;
  onProductClick: (p: MenuProduct) => void;
}) {
  const price = Number(product.price);
  const originalPrice = Number(product.original_price);
  const hasDiscount = product.discount_applied && product.savings > 0;
  const ctrl = useProductCartControl(storeUlid, product, () => onProductClick(product));

  return (
    <div
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onProductClick(product); }}
      onClick={() => onProductClick(product)}
      className="group flex w-full items-stretch gap-4 rounded-2xl border border-gray-200 dark:border-[#313131] p-4 text-left shadow-sm transition hover:border-gray-300 dark:hover:border-[#444] hover:shadow-md cursor-pointer"
    >
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-1.5 py-1 pl-1">
        <h3 className="text-[17px] font-bold text-gray-900 dark:text-white line-clamp-1 leading-tight tracking-tight">{product.name}</h3>
        {product.description && (
          <p className="line-clamp-2 text-[15px] leading-snug text-gray-500 dark:text-gray-400">{product.description}</p>
        )}
        <div className="mt-0.5 flex items-baseline gap-2">
          <span className="text-[15px] font-bold text-gray-700 dark:text-gray-200">
            {formatAmount(hasDiscount ? product.discounted_price : price)} DA
          </span>
          {hasDiscount && (
            <span className="text-xs text-gray-400 dark:text-gray-500 line-through">{formatAmount(originalPrice)} DA</span>
          )}
        </div>
      </div>

      <div className="relative h-[130px] w-[150px] shrink-0 self-center overflow-hidden rounded-2xl bg-gray-100 dark:bg-[#313131]">
        <Image
          src={product.image ?? "/fast-logo.svg"}
          alt={product.name}
          fill
          className={product.image ? "object-cover transition group-hover:scale-105" : "object-contain p-6 opacity-20 dark:opacity-10"}
          sizes="150px"
        />
        <div className="absolute bottom-2.5 right-2.5">
          <AddControl cartQty={ctrl.cartQty} showStepper={ctrl.showStepper} busy={ctrl.busy} onPlus={ctrl.onPlus} onMinus={ctrl.onMinus} size="md" />
        </div>
      </div>

      {ctrl.cartAuthModal}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  PRODUCT MODAL                                                */
/* ══════════════════════════════════════════════════════════════ */

function ProductModal({
  product,
  storeUlid,
  onClose,
}: {
  product: MenuProduct;
  storeUlid: string;
  onClose: () => void;
}) {
  const [qty, setQty] = useState(Math.max(1, product.minimum_quantity ?? 1));
  const max = product.maximum_quantity > 0 ? product.maximum_quantity : 99;
  const min = Math.max(1, product.minimum_quantity ?? 1);
  const basePrice = Number(product.price);
  const hasDiscount = product.discount_applied && product.savings > 0;
  const unitPrice = hasDiscount ? product.discounted_price : basePrice;
  const discountPct = hasDiscount && basePrice > 0 ? Math.round((product.savings / basePrice) * 100) : 0;
  const [selectedOptions, setSelectedOptions] = useState<Record<string, Set<string>>>({});
  const [showErrors, setShowErrors] = useState(false);

  const { data: addons, isLoading: addonsLoading } = useProductAddons(storeUlid, product.ulid);
  const addToCart = useAddToCart();
  const updateItem = useUpdateCartItem();
  const { data: storeCart } = useStoreCart(storeUlid);
  // Le panier est réservé aux personnes connectées : sans ce garde, un invité
  // déclenchait l'appel, récupérait un 401 et se faisait rediriger vers la
  // connexion — panier vide et options choisies perdues. On demande le numéro
  // AVANT d'ajouter, et l'ajout se rejoue une fois l'OTP validé.
  const user = useAuthStore((st) => st.user);
  const cartAuth = useCartAuthGate();

  const addonsArray = Array.isArray(addons) ? addons : [];
  const addonExtra = addonsArray.reduce((sum, addon) => {
    const sel = selectedOptions[addon.ulid];
    if (!sel || sel.size === 0) return sum;
    return sum + addon.options
      .filter((o) => sel.has(o.ulid))
      .reduce((s, o) => s + (o.discounted_price ?? Number(o.price) ?? 0), 0);
  }, 0);

  const total = (unitPrice + addonExtra) * qty;
  const isOutOfStock = isProductOutOfStock(product);

  // Required addon groups must each have at least the minimum number of selections.
  const isAddonUnmet = (addon: (typeof addonsArray)[number]) => {
    if (!addon.minimum_selection_required) return false;
    const minSel = Math.max(1, addon.minimum_selection ?? 1);
    return (selectedOptions[addon.ulid]?.size ?? 0) < minSel;
  };
  const firstUnmetUlid = addonsArray.find(isAddonUnmet)?.ulid ?? null;
  const requiredUnmet = firstUnmetUlid !== null;

  // Only surface a stock badge when the API actually reports a usable count —
  // otherwise every inventory-tracked product would read as "Épuisé".
  const remaining = trackedStock(product);
  const stockLabel = isOutOfStock
    ? { text: "Épuisé", cls: "text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-[#313131]" }
    : remaining === null
      ? null
      : remaining <= 5
        ? { text: `${remaining} en stock`, cls: "text-orange-600 bg-orange-50" }
        : { text: `${remaining > 20 ? "20+" : remaining} en stock`, cls: "text-green-700 bg-green-50" };

  function toggleOption(addonUlid: string, optionUlid: string, maxSelect?: number, minRequired?: boolean) {
    setSelectedOptions((prev) => {
      const current = new Set(prev[addonUlid] ?? []);
      if (current.has(optionUlid)) {
        if (minRequired && maxSelect === 1) return prev;
        current.delete(optionUlid);
      } else {
        if (maxSelect === 1) current.clear();
        if (!maxSelect || current.size < maxSelect) current.add(optionUlid);
      }
      return { ...prev, [addonUlid]: current };
    });
  }

  function buildAddonsPayload(): AddToCartPayload["addons"] {
    const result: NonNullable<AddToCartPayload["addons"]> = [];
    for (const addon of addonsArray) {
      const sel = selectedOptions[addon.ulid];
      if (!sel || sel.size === 0) continue;
      result.push({
        addon_ulid: addon.ulid,
        options: [...sel].map((optUlid) => ({ addon_option_ulid: optUlid, quantity: 1 })),
      });
    }
    return result.length > 0 ? result : undefined;
  }

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full sm:max-w-md bg-white dark:bg-[#2A2A2A] sm:rounded-2xl rounded-t-2xl flex flex-col max-h-[92vh] overflow-hidden">

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">

          {/* Full-width image (omitted entirely when there is no image) */}
          {product.image ? (
            <div className="relative w-full aspect-[4/3] bg-gray-50 dark:bg-[#1E1E1E] overflow-hidden">
              <Image src={product.image} alt={product.name} fill className="object-cover" sizes="(max-width: 640px) 100vw, 448px" priority />
              {hasDiscount && (
                <div className="absolute top-4 left-4 flex items-center gap-2">
                  {discountPct > 0 && (
                    <span className="rounded-lg bg-red-500 px-2.5 py-1 text-[12px] font-bold text-white leading-none shadow-sm">Réduction de {discountPct}%</span>
                  )}
                  <span className="rounded-lg bg-secondary px-2.5 py-1 text-[12px] font-bold text-white leading-none shadow-sm">Économisez {formatAmount(product.savings)} DA</span>
                </div>
              )}
              <button onClick={onClose} className="absolute top-4 right-4 flex h-9 w-9 items-center justify-center rounded-full bg-white dark:bg-[#2A2A2A] text-gray-700 dark:text-gray-300 shadow-md transition hover:bg-gray-100 dark:hover:bg-[#313131]">
                <X className="h-5 w-5" strokeWidth={2} />
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-end px-5 pt-5">
              <button onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-full bg-white dark:bg-[#2A2A2A] text-gray-700 dark:text-gray-300 shadow-sm border border-gray-100 dark:border-[#313131] transition hover:bg-gray-100 dark:hover:bg-[#313131]">
                <X className="h-5 w-5" strokeWidth={2} />
              </button>
            </div>
          )}

          {/* Name + price */}
          <div className="px-5 pt-5">
            <h3 className="text-2xl font-extrabold text-gray-900 dark:text-white leading-snug">{product.name}</h3>
            <div className="mt-2 flex items-baseline gap-2 flex-wrap">
              <span className={`text-2xl font-extrabold ${hasDiscount ? "text-red-500" : "text-gray-900 dark:text-white"}`}>{formatAmount(unitPrice)} DA</span>
              {hasDiscount && (
                <span className="text-base text-gray-400 dark:text-gray-500 line-through">{formatAmount(basePrice)} DA</span>
              )}
            </div>

            {stockLabel && (
              <div className="mt-3">
                <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[12px] font-semibold ${stockLabel.cls}`}>
                  {!isOutOfStock && <span className="h-1.5 w-1.5 rounded-full bg-current inline-block" />}
                  {stockLabel.text}
                </span>
              </div>
            )}

            {product.description && (
              <p className="mt-3 text-[15px] text-gray-600 dark:text-gray-400 leading-relaxed">{product.description}</p>
            )}
          </div>

          {/* Addons skeleton */}
          {addonsLoading && (
            <div className="px-5 py-5 mt-2 space-y-6 animate-pulse">
              {[1, 2].map((g) => (
                <div key={g}>
                  <div className="h-5 w-40 rounded bg-gray-200 dark:bg-[#313131] mb-2" />
                  <div className="h-3 w-32 rounded bg-gray-100 dark:bg-[#313131] mb-4" />
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-3 py-3">
                      <div className="h-5 w-5 rounded bg-gray-200 dark:bg-[#313131]" />
                      <div className="h-4 w-32 rounded bg-gray-100 dark:bg-[#313131]" />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {/* Addon groups */}
          {!addonsLoading && addonsArray.length > 0 && (
            <div className="mt-4 divide-y divide-gray-100 dark:divide-[#313131] border-t border-gray-100 dark:border-[#313131]">
              {addonsArray.map((addon) => {
                const showAddonError = showErrors && isAddonUnmet(addon);
                return (
                <div
                  key={addon.ulid}
                  id={`addon-${addon.ulid}`}
                  className={`scroll-mt-4 px-5 py-4 ${showAddonError ? "bg-red-50/60 dark:bg-red-950/20" : ""}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-lg font-bold text-gray-900 dark:text-white">{addon.name}</p>
                    {addon.minimum_selection_required ? (
                      <span className={`rounded-md px-2.5 py-1 text-[11px] font-bold leading-none ${showAddonError ? "bg-red-500 text-white" : "bg-secondary text-white"}`}>Obligatoire</span>
                    ) : (
                      <span className="text-xs text-gray-400 dark:text-gray-500">Optionnel</span>
                    )}
                  </div>
                  <p className={`mt-1 mb-2 text-[13px] ${showAddonError ? "font-semibold text-red-500" : "text-gray-400 dark:text-gray-500"}`}>
                    {showAddonError
                      ? "Veuillez sélectionner une option"
                      : addon.maximum_selection === 1
                        ? "Sélectionner 1 option"
                        : `Sélectionner ${addon.minimum_selection_required ? "au moins 1" : "jusqu'à " + addon.maximum_selection} option(s)`}
                  </p>
                  <div className="divide-y divide-gray-50 dark:divide-[#313131]">
                    {addon.options.map((opt) => {
                      const checked = (selectedOptions[addon.ulid] ?? new Set()).has(opt.ulid);
                      const optPrice = opt.discounted_price ?? Number(opt.price ?? 0);
                      const isRadio = addon.maximum_selection === 1;
                      return (
                        <label
                          key={opt.ulid}
                          className="flex cursor-pointer items-center gap-3 py-3.5 transition"
                          onClick={() => toggleOption(addon.ulid, opt.ulid, addon.maximum_selection, addon.minimum_selection_required)}
                        >
                          <span className={`flex h-6 w-6 shrink-0 items-center justify-center transition ${isRadio ? `rounded-full border-2 ${checked ? "border-primary bg-primary" : "border-gray-300 dark:border-gray-600 bg-white dark:bg-[#2A2A2A]"}` : `rounded-md border-2 ${checked ? "border-primary bg-primary" : "border-gray-300 dark:border-gray-600 bg-white dark:bg-[#2A2A2A]"}`}`}>
                            {checked && isRadio && <span className="h-2.5 w-2.5 rounded-full bg-white" />}
                            {checked && !isRadio && (
                              <Check className="h-3.5 w-3.5 text-secondary dark:text-primary" strokeWidth={2.5} />
                            )}
                          </span>
                          <span className="flex-1 text-[15px] text-gray-900 dark:text-gray-200">{opt.name}</span>
                          {optPrice > 0 && (
                            <span className="text-sm text-gray-500 dark:text-gray-400 font-medium">+{formatAmount(optPrice)} DA</span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 dark:border-[#313131] px-4 py-4 flex items-center gap-3 bg-white dark:bg-[#2A2A2A] shrink-0">
          <div className="flex items-center gap-3 rounded-full border border-gray-200 dark:border-[#313131] px-2 py-1.5">
            <button onClick={() => setQty((q) => Math.max(min, q - 1))} disabled={qty <= min}
              className="flex h-8 w-8 items-center justify-center rounded-full text-gray-700 dark:text-gray-400 transition hover:bg-gray-100 dark:hover:bg-[#313131] disabled:opacity-40">
              <Minus className="h-4 w-4" strokeWidth={2.5} />
            </button>
            <span className="w-5 text-center text-base font-bold text-gray-900 dark:text-white">{qty}</span>
            <button onClick={() => setQty((q) => Math.min(max, q + 1))} disabled={qty >= max}
              className="flex h-8 w-8 items-center justify-center rounded-full text-gray-700 dark:text-gray-400 transition hover:bg-gray-100 dark:hover:bg-[#313131] disabled:opacity-40">
              <Plus className="h-4 w-4" strokeWidth={2.5} />
            </button>
          </div>
          <button
            onClick={() => {
              if (requiredUnmet) {
                // Don't add — surface the unfilled required group and scroll to it.
                setShowErrors(true);
                if (firstUnmetUlid) {
                  document.getElementById(`addon-${firstUnmetUlid}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
                }
                return;
              }
              const addonsPayload = buildAddonsPayload();
              // If this exact product + option combo is already in the cart, bump
              // that line instead of adding a second identical one. (Un invité
              // n'a pas de panier : `existing` est forcément absent.)
              const existing = findMatchingCartItem(storeCart?.items, product.ulid, addonsPayload);
              if (existing) {
                updateItem.mutate(
                  {
                    storeUlid,
                    cartItemId: existing.cart_item_id,
                    payload: { cart_item_id: existing.cart_item_id, quantity: existing.quantity + qty },
                  },
                  { onSuccess: () => onClose() }
                );
                return;
              }
              const payload = {
                product_ulid: product.ulid,
                quantity: qty,
                store_ulid: storeUlid,
                addons: addonsPayload,
                tracking: { name: product.name, price: unitPrice + addonExtra },
              };
              if (!user) {
                cartAuth.requireAuth(payload, () =>
                  addToCart.mutate(payload, { onSuccess: () => onClose() }),
                );
                return;
              }
              addToCart.mutate(payload, { onSuccess: () => onClose() });
            }}
            disabled={addToCart.isPending || updateItem.isPending || isOutOfStock}
            className="flex flex-1 items-center justify-between gap-2 rounded-full bg-primary px-5 py-3.5 text-base font-bold text-secondary transition hover:opacity-90 active:scale-[0.99] disabled:opacity-50"
          >
            <span>{isOutOfStock ? "Épuisé" : "Ajouter au panier"}</span>
            {!isOutOfStock && <span>{formatAmount(total)} DA</span>}
          </button>
        </div>
      </div>
      {cartAuth.modal}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  SKELETON                                                     */
/* ══════════════════════════════════════════════════════════════ */

function StoreDetailSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="mx-auto px-4 md:px-6 lg:px-8">
        <div className="mt-4 h-[300px] rounded-2xl bg-gray-200 dark:bg-[#313131]" />
        <div className="flex items-start gap-6 border-b border-gray-100 dark:border-[#313131] py-5">
          <div className="flex-1 space-y-2">
            <div className="h-7 w-64 rounded bg-gray-200 dark:bg-[#313131]" />
            <div className="h-4 w-48 rounded bg-gray-100 dark:bg-[#313131]" />
          </div>
          <div className="hidden md:block h-10 w-[280px] rounded-full bg-gray-100 dark:bg-[#313131]" />
        </div>
        <div className="py-6 space-y-4">
          <div className="flex gap-3">
            <div className="h-10 w-48 rounded-full bg-gray-100 dark:bg-[#313131]" />
            <div className="ml-auto flex gap-3">
              <div className="h-10 w-28 rounded-xl bg-gray-100 dark:bg-[#313131]" />
              <div className="h-10 w-28 rounded-xl bg-gray-100 dark:bg-[#313131]" />
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-8">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex gap-4 rounded-xl border border-gray-100 dark:border-[#313131] p-3">
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-3/4 rounded bg-gray-200 dark:bg-[#313131]" />
                  <div className="h-3 w-full rounded bg-gray-100 dark:bg-[#313131]" />
                  <div className="h-3 w-1/3 rounded bg-gray-100 dark:bg-[#313131]" />
                </div>
                <div className="h-[110px] w-[128px] shrink-0 rounded-xl bg-gray-200 dark:bg-[#313131]" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}